// Iter 287 — Modul Mockup Shopee (React).
// Menuruti pola Nota Offline untuk upload/preview/download. Terpisah dari alur produksi.
import { useEffect, useMemo, useRef, useState } from "react";
import { MockupAiRead, CardAiStatus, useAiFileReads } from "./mockup_ai_read";
import { useAutoRefresh } from "./use_auto_refresh";
import { createPortal } from "react-dom";
import axios from "axios";
import { guardedUpload, withQuery, usePrecheck, GuardPanel, showOrderNoCheck } from "./upload_guard";
import { canProceed, guardParams } from "./upload_guard_logic";
import { PaperFormatToggle, FormatBadge, setPaperFormat, FORMAT_LABEL } from "./paper_format";
import {
  Package, Filter, Search, Upload, Image as ImgIcon, Layers,
  Users as UsersIcon, ArrowLeft, Plus, RefreshCw, CheckCircle2,
  AlertCircle, XCircle, Clock, Printer, ChevronRight, Copy, Ban, Loader2,
  ClipboardPaste, Send, Warehouse as WarehouseIcon, MoreHorizontal, Eye, PenSquare,
  X, Info, History, Lock
} from "lucide-react";
import { RiwayatPekerjaanModal } from "./riwayat_pekerjaan";
import { HoldKebabMenu } from "./hold_cancel_panel";
import { LeaderboardMockupShopee } from "./leaderboard_mockup_shopee";
import { ShopeeMockupQueuePanel } from "./shopee_mockup_queue";
import "./shopee_workspace.css";
import { ShopeeDeadlineChip, deadlineMs } from "./shopee_deadline_chip";
import { notify } from "./modal_center";
import { toast } from "sonner";
import { TokopediaAttachments } from "./channel";
import { TokopediaDeadline } from "./tokopedia_deadline";
import { useChannel as useTkpChannel } from "./channel";
import { DesignFormButton } from "./design_form";
import { FormCheckFix, fetchFormCheck, formCheckSummary, needsFixReason } from "./form_mockup_check";
import { MockupTemplateButton } from "./mockup_template_button";
import { getChannel as _dfmChannel } from "./channel";
import { RepeatOrderBadge, RepeatOrderFields } from "./repeat_order";
import { DesignerHistoryChip } from "./designer_history_chip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Iter 287ac — Normalisasi error API menjadi string.
// FastAPI/Pydantic v2 mengembalikan `detail` sebagai array objek validasi
// ({type, loc, msg, input, url}) → jika di-render langsung React akan crash.
// Fungsi ini selalu menghasilkan string ringkas yang aman dipakai di toast/modal.
function formatApiError(e, fallback = "Terjadi kesalahan tidak diketahui.") {
  const d = e?.response?.data?.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (Array.isArray(d)) {
    // Ambil hanya field terakhir (setelah "body.") + msg, buang input mentah.
    const parts = d.map(it => {
      const msg = it?.msg || "Validasi gagal";
      const loc = Array.isArray(it?.loc) ? it.loc.filter(x => x !== "body") : [];
      const field = loc.length ? loc.join(".") : "";
      return field ? `${field}: ${msg}` : msg;
    });
    return parts.join("; ");
  }
  if (d && typeof d === "object") {
    return d.msg || d.message || JSON.stringify(d);
  }
  return e?.message || fallback;
}

const STATUS_LABELS = {
  BELUM_FIX: { t: "Belum Fix", c: "#000000", bg: "#FACC15", Icon: Clock },
  REVISI: { t: "Revisi", c: "#000000", bg: "#F97316", Icon: PenSquare },
  FIX: { t: "Fix", c: "#000000", bg: "#A3E635", Icon: CheckCircle2 },
  BATAL: { t: "Batal", c: "#FFFFFF", bg: "#DC2626", Icon: Ban },
};

// Iter 287ae — Pilihan Proses Pengerjaan Shopee.
export const SHOPEE_PROCESS_OPTIONS = [
  { value: "bordir", label: "Bordir" },
  { value: "sablon_dtf", label: "Sablon DTF" },
  { value: "bordir_sablon_dtf", label: "Bordir + Sablon DTF" },
  { value: "sublim_full_print", label: "Sublim/Full Print" },
];
export const SHOPEE_PROCESS_LABEL = SHOPEE_PROCESS_OPTIONS.reduce((m, o) => (m[o.value] = o.label, m), {});
export const SHOPEE_PROCESSES_WITH_EMBROIDERY = new Set(["bordir", "bordir_sablon_dtf"]);

function fmtDate(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

function fmtDurationSince(iso, endIso) {
  if (!iso) return "-";
  try {
    const a = new Date(iso).getTime();
    const b = endIso ? new Date(endIso).getTime() : Date.now();
    if (!Number.isFinite(a) || !Number.isFinite(b)) return "-";
    const totalSec = Math.floor((b - a) / 1000);
    if (totalSec < 0) return "-";
    const d = Math.floor(totalSec / 86400);
    const h = Math.floor((totalSec % 86400) / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    const parts = [];
    if (d) parts.push(`${d} hari`);
    if (h) parts.push(`${h} jam`);
    if (m) parts.push(`${m} mnt`);
    if (s || parts.length === 0) parts.push(`${s} dtk`);
    return parts.join(" ");
  } catch { return "-"; }
}

// Iter 394 — P0 fix: Sumber kebenaran status "sudah diteruskan ke Gudang".
// Sebelumnya kode hanya mengecek `warehouse_state === "sent"`, tetapi setelah
// Gudang ACC Barang Siap backend set `warehouse_state = "completed"`. Akibatnya
// tombol "Kirim ke Gudang" muncul kembali di antrean Mockup → bug duplikasi
// pengiriman. Treat kedua state ini sebagai forwarded.
const WAREHOUSE_FORWARDED_STATES = new Set(["sent", "completed"]);
const isWarehouseForwarded = (job) => WAREHOUSE_FORWARDED_STATES.has(job?.warehouse_state);
const isWarehouseCompleted = (job) => job?.warehouse_state === "completed";
const SHOPEE_CANCELLED = new Set(["cancelled", "canceled"]);
const SHOPEE_CANCEL_PENDING = new Set(["in_cancel", "cancelling"]);
export const isCancelPending = (j) =>
  (j?.pending_request?.status === "pending" && j.pending_request.kind === "cancel")
  || (j?.hold?.active && j.hold.kind === "cancel" && j.hold.status === "pending")
  || SHOPEE_CANCEL_PENDING.has(j?.shopee_order_status);
export const isCancelledJob = (j) =>
  j?.cancellation_status === "canceled_internal" || SHOPEE_CANCELLED.has(j?.shopee_order_status)
  || (j?.status === "BATAL" && !isCancelPending(j));
// Alasan Kirim ke Gudang belum bisa (null = boleh). Sama dengan guard backend send-to-warehouse + HOLD/BATAL.
export function sendBlock(job, hasMockup) {
  if (job?.hold?.active) return { reason: "Kirim ke Gudang dikunci: pekerjaan sedang HOLD. Kirim setelah HOLD dibuka." };
  if (isCancelPending(job) || isCancelledJob(job)) return { reason: "Kirim ke Gudang dikunci: pesanan dibatalkan / menunggu keputusan pembatalan." };
  if (!hasMockup) return { reason: "Belum bisa dikirim ke Gudang: belum ada mockup aktif. Tambah mockup terlebih dahulu.", fix: "add" };
  if (!(job?.process || "").trim()) return { reason: "Belum bisa dikirim ke Gudang: Proses Pengerjaan belum dipilih. Isi lewat Ubah Pekerjaan.", fix: "edit" };
  return null;
}
// Sudah dikirim ke Gudang lalu direvisi (belum FIX, tidak HOLD) → kembali ke Pekerjaan Saya (sama dengan backend list_jobs).
export const needsRework = (j) => j?.warehouse_state === "sent" && j?.status !== "FIX" && j?.status !== "BATAL" && !j?.hold?.active;
const isActiveJob = (j) => !j.transferred_from_me && (!isWarehouseForwarded(j) || needsRework(j)) && !isCancelledJob(j);

// Peringatan 24 jam: dihitung dari waktu rekap (created_at pekerjaan = saat diambil/dibuat Desainer), bukan tanggal pesanan.
//   Hilang hanya bila warehouse_state tersimpan sent/completed. waiting = HOLD/pengajuan (bukan kelalaian PIC).
export function staleInfo(j, now = Date.now()) {
  if (!isActiveJob(j) || j.is_test) return null;
  const t = Date.parse(j.created_at || "");
  if (!t) return { incomplete: true };
  const hours = Math.floor((now - t) / 3600000);
  if (hours < 24) return null;
  const waiting = !!(j.hold?.active || isCancelPending(j) || j.pending_request?.status === "pending");
  const hasMockup = (j.mockups || []).some(m => !m.deleted_at);
  const fixBlock = j.status === "FIX" ? sendBlock(j, hasMockup) : null;
  const action = waiting ? "Tertahan — menunggu keputusan HOLD/pengajuan"
    : j.status === "FIX" ? (fixBlock?.fix === "add" ? "FIX — tambah mockup dulu" : fixBlock?.fix === "edit" ? "FIX — isi Proses Pengerjaan" : "FIX — segera Kirim ke Gudang")
    : j.status === "REVISI" ? "Revisi — perbaiki lalu FIX & kirim"
    : hasMockup ? "Mockup belum FIX — Tandai FIX" : "Belum ada mockup — buat mockup";
  return { hours, waiting, action };
}

// Ukuran kertas pekerjaan: tersimpan (paper_pref) → format file aktif yang tersimpan (pekerjaan lama) → A5.
export function jobPaper(job) {
  if (job?.paper_pref) return job.paper_pref;
  const fm = (job?.mockups || []).filter(m => !m.deleted_at)
    .map(m => (m.versions || []).find(v => v.version === m.current_version)?.paper_format).filter(Boolean);
  return fm.length && fm.every(f => f === fm[0]) ? fm[0] : "A5L";
}

export function StaleBadge({ job }) {
  const st = staleInfo(job);
  if (!st) return null;
  if (st.incomplete) return <div className="shp-stale is-info" data-testid={`shopee-stale-${job.id}`}>Waktu rekap tidak tercatat — data belum lengkap</div>;
  return <div className={`shp-stale ${st.waiting ? "is-wait" : "is-late"}`} data-testid={`shopee-stale-${job.id}`}
    title={`${st.hours} jam sejak rekap (dibuat ${job.created_at || "-"}) · belum dikirim ke Gudang`}>
    <b>{st.hours} jam sejak rekap</b> · {st.action}</div>;
}
const isOver24 = (j) => { const st = staleInfo(j); return !!(st && !st.incomplete && !st.waiting); };
// Waktu klaim = created_at pekerjaan (dibuat saat diambil dari antrean); terbaru dulu.
export const byRecentClaim = (a, b) => (Date.parse(b.created_at || 0) || 0) - (Date.parse(a.created_at || 0) || 0);

export default function ShopeeModule({ onBack, embedded = false }) {
  const [me, setMe] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filters, setFilters] = useState({ status: "", designer_id: "", job_type: "", q: "", date_from: "", date_to: "" });
  // Iter 287aj — Urutan tampilan kartu di daftar. Tidak mempengaruhi KPI (yang
  // memo `counts` tetap dari `jobs`). Nilai: "recent" | "todo" | "waiting" | "hold".
  // Default: pekerjaan yang paling baru DIAMBIL di atas (waktu klaim = created_at pekerjaan). Prioritas tetap bisa dipilih manual.
  const [sortBy, setSortBy] = useState("recent");
  const [onlyOver24, setOnlyOver24] = useState(false);
  const [viewMode, setViewMode] = useState(() => { try { return localStorage.getItem("tamfaris.shopee.viewMode") || "card"; } catch { return "card"; } });
  const setViewModePersist = (m) => { setViewMode(m); try { localStorage.setItem("tamfaris.shopee.viewMode", m); } catch { /* abaikan */ } };
  const [showForm, setShowForm] = useState(false);  const [detailId, setDetailId] = useState(null);
  const [showRekap, setShowRekap] = useState(false);
  // Iter 358 — Panel Riwayat Pekerjaan (read-only overview semua pekerjaan PIC
  //   termasuk BATAL/FIX/dikirim-ke-Gudang). Tidak mengubah workflow aktif.
  const [showRiwayat, setShowRiwayat] = useState(false);
  const [riwayatStatus, setRiwayatStatus] = useState("");
  // Iter 469 — fetch terbaru (silent) saat Riwayat dibuka.
  const openRiwayat = (st) => {
    if (histTruncated) histLimitRef.current = 3000;
    loadJobs({ force: true }); setRiwayatStatus(st); setShowRiwayat(true);
  };
  // Iter 287w — Aksi cepat kartu (tanpa reload seluruh halaman).
  const [busyJobId, setBusyJobId] = useState(null);
  const [busyKind, setBusyKind] = useState("");
  const [previewForJob, setPreviewForJob] = useState(null); // job → tampilkan thumbnail preview
  const [quickPaste, setQuickPaste] = useState(null); // { job, preview:{file,url} }
  const [quickAddJob, setQuickAddJob] = useState(null); // modal Tambah Mockup: Ctrl+V / Tempel / Pilih File
  const [quickReqHold, setQuickReqHold] = useState(null); // { job, kind }
  // Iter 468 — Auto-refresh: inflight guard + background mode supaya auto-refresh
  //   setiap 30s tidak reset scroll/filter/pencarian dan tidak membuat loading
  //   skeleton flicker.
  const inflightRef = useRef(false);
  const reqSeqRef = useRef(0);
  const jobsRef = useRef([]);
  const lastScopeRef = useRef("");
  jobsRef.current = jobs;
  // Batas riwayat (pekerjaan aktif selalu lengkap dari backend). Naik permanen saat Riwayat dibuka & terpotong.
  const histLimitRef = useRef(500);
  const [histTruncated, setHistTruncated] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [quickCancel, setQuickCancel] = useState(null); // job
  const quickFileRef = useRef(null);
  const [quickFileJob, setQuickFileJob] = useState(null);
  // Iter 495 — Modal "Edit Pekerjaan" (bukan "Ubah Desain"/detail). Mengedit
  //   metadata pekerjaan existing (customer, judul, proses, jenis, keterangan,
  //   repeat order, PIC bila supervisor) via PATCH /shopee/{id} tanpa mengubah
  //   workflow/warehouse/history produksi. Berbeda dari "Ubah Desain" yang
  //   membuka detail untuk edit file mockup.
  const [editJob, setEditJob] = useState(null);
  // Iter 395 — Guard anti double-click untuk aksi kirim-ke-gudang. Set true
  // sejak user menekan tombol (sebelum modal konfirmasi terbuka), reset
  // setelah selesai/dibatalkan/gagal. Mencegah dua request paralel.
  const sendingRef = useRef(new Set());
  const patchJob = (updated) => setJobs(prev => prev.map(x => x.id === updated.id ? { ...x, ...updated,
    ...(updated.shopee_deadline_source ? {} : { shopee_deadline_iso: x.shopee_deadline_iso, shopee_deadline_source: x.shopee_deadline_source, shopee_order_status: x.shopee_order_status, shopee_linked: x.shopee_linked }) } : x));
  const handleQuickAction = async (action, job, payload) => {
    if (action === "detail") return setDetailId(job.id);
    if (action === "edit") return setEditJob(job);
    if (action === "test_flag") {
      const on = !job.is_test;
      const ok = await notify.confirm({ title: on ? "Tandai sebagai UJI?" : "Cabut tanda UJI?",
        message: on ? `${job.order_number} tidak dihitung terlambat, tidak masuk KPI desainer, dan tidak tampil di antrean Gudang utama. Status & data lain tidak berubah.`
          : `${job.order_number} kembali dihitung sebagai pekerjaan nyata.`, primaryLabel: on ? "Tandai UJI" : "Cabut UJI", secondaryLabel: "Batal" });
      if (!ok) return;
      try { const r = await axios.post(`${API}/shopee/${job.id}/test-flag`, { is_test: on }); if (r?.data) patchJob(r.data); }
      catch (e) { notify.error({ title: "Gagal", message: formatApiError(e, "Gagal mengubah tanda UJI.") }); }
      return;
    }
    if (action === "preview") return setPreviewForJob(job);
    if (action === "add") return setQuickAddJob(job);
    if (action === "paste") {
      try {
        const items = await navigator.clipboard.read();
        for (const it of items) {
          const type = it.types.find(t => t.startsWith("image/"));
          if (type) {
            const blob = await it.getType(type);
            const file = new File([blob], `paste-${Date.now()}.png`, { type });
            setQuickPaste({ job, preview: { file, url: URL.createObjectURL(file), source: "pick" } });
            return;
          }
        }
        notify.warning({ title: "Clipboard kosong", message: "Tidak ada gambar untuk ditempel." });
      } catch {
        notify.warning({ title: "Akses clipboard ditolak", message: "Izinkan akses clipboard di browser atau gunakan Tambah Mockup." });
      }
      return;
    }
    if (action === "fix") {
      const st = await fetchFormCheck(job.id).catch(() => null);
      let checkReason = "";
      if (needsFixReason(st)) {
        checkReason = await notify.prompt({
          title: "Tetap Tandai FIX?",
          message: `${job.order_number}\n${formCheckSummary(st)}\n\nPengecualian: alasan, nama, dan waktu dicatat untuk Owner.`,
          placeholder: "Alasan tetap FIX (min. 5 karakter)",
          multiline: true, required: true,
          primaryLabel: "Tetap FIX", secondaryLabel: "Batal",
        });
        if (!checkReason || checkReason.trim().length < 5) {
          if (checkReason) notify.warning({ title: "Alasan terlalu pendek", message: "Isi minimal 5 karakter." });
          return;
        }
      } else {
        const ok = await notify.confirm({
          title: "Tandai FIX?",
          message: `${st?.state === "sesuai" ? "Formulir vs Mockup: Sesuai. " : ""}Set status ${job.order_number} menjadi FIX. Setelah FIX, tombol Kirim ke Gudang tersedia.`,
          primaryLabel: "Ya, tandai FIX",
          secondaryLabel: "Batal",
          tone: "success",
        });
        if (!ok) return;
      }
      try {
        setBusyJobId(job.id); setBusyKind("fix");
        const r = await axios.post(`${API}/shopee/${job.id}/status`, { status: "FIX", check_reason: checkReason.trim() });
        patchJob(r.data);
        notify.success({ title: "Berhasil", message: `${job.order_number} → FIX` });
      } catch (e) {
        notify.error({ title: "Gagal tandai FIX", message: formatApiError(e, "Gagal menandai FIX. Coba lagi.") });
      } finally { setBusyJobId(null); setBusyKind(""); }
      return;
    }
    if (action === "cancel") { setQuickCancel(job); return; }
    if (action === "revise") {
      // Iter 287ag — Revisi cepat: FIX → REVISI. Aturan lock backend tetap dipatuhi
      // (bila locked/processing, backend akan menolak — user seharusnya lihat "Ajukan Revisi").
      const ok = await notify.confirm({
        title: "Ubah ke Revisi?",
        message: `Status ${job.order_number} akan diturunkan ke REVISI supaya bisa diedit ulang.`,
        primaryLabel: "Ya, ubah",
        secondaryLabel: "Batal",
        tone: "warning",
      });
      if (!ok) return;
      try {
        setBusyJobId(job.id); setBusyKind("revise");
        const r = await axios.post(`${API}/shopee/${job.id}/status`, { status: "REVISI" });
        patchJob(r.data);
        notify.success({ title: "Berhasil", message: `${job.order_number} → REVISI` });
      } catch (e) {
        notify.error({ title: "Gagal ubah ke Revisi", message: formatApiError(e, "Gagal mengubah status.") });
      } finally { setBusyJobId(null); setBusyKind(""); }
      return;
    }
    if (action === "unfix") {
      // Iter 485 — Kembalikan status ke BELUM_FIX dari FIX/REVISI.
      // Aturan lock backend tetap dipatuhi (processing → 423, pending → dijaga di sisi UI).
      const ok = await notify.confirm({
        title: "Kembalikan ke Belum Fix?",
        message: `Status ${job.order_number} akan dikembalikan ke BELUM_FIX supaya desain bisa dikerjakan ulang.`,
        primaryLabel: "Ya, kembalikan",
        secondaryLabel: "Batal",
        tone: "warning",
      });
      if (!ok) return;
      try {
        setBusyJobId(job.id); setBusyKind("unfix");
        const r = await axios.post(`${API}/shopee/${job.id}/status`, { status: "BELUM_FIX" });
        patchJob(r.data);
        notify.success({ title: "Berhasil", message: `${job.order_number} → BELUM_FIX` });
      } catch (e) {
        notify.error({ title: "Gagal kembalikan ke Belum Fix", message: formatApiError(e, "Gagal mengubah status.") });
      } finally { setBusyJobId(null); setBusyKind(""); }
      return;
    }
    if (action === "request_hold" || action === "request_cancel") {
      setQuickReqHold({ job, kind: action === "request_hold" ? "hold" : "cancel" });
      return;
    }
  };

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const [meR, jobsR, dsR] = await Promise.all([
        axios.get(`${API}/shopee/me`),
        axios.get(`${API}/shopee/jobs`, { params: Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) })
          .catch(err => { throw err; }),
        // Endpoint list is /shopee, not /shopee/jobs; adjust
      ]);
      setMe(meR.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Gagal memuat");
    } finally { setLoading(false); }
  };

  const loadJobs = async (opts = {}) => {
    const silent = !!opts.silent;
    // Iter 468 — Guard inflight: cegah duplicate request saat auto-refresh
    //   bentrok dengan klik tombol atau fetch after-action.
    // Auto-refresh dilewati bila ada request berjalan; request pengguna (filter/aksi) SELALU jalan.
    //   Hanya respons dari request TERBARU yang boleh menimpa daftar (anti respons lama menimpa baru).
    if (silent && inflightRef.current) return;
    inflightRef.current = true;
    const seq = ++reqSeqRef.current;
    if (silent || opts.force) setRefreshing(true); else { setLoading(true); setErr(""); }
    try {
      const params = {};
      // Iter 287ab — KPI selalu mencerminkan seluruh status dalam scope.
      // Kirim SEMUA filter kecuali `status`; filter status diterapkan client-side.
      Object.entries(filters).forEach(([k, v]) => { if (v && k !== "status") params[k] = v; });
      params.limit = histLimitRef.current;
      const [meR, jR, dR] = await Promise.all([
        me ? Promise.resolve({ data: me }) : axios.get(`${API}/shopee/me`),
        axios.get(`${API}/shopee`, { params }),
        axios.get(`${API}/shopee/designers`),
      ]);
      if (seq !== reqSeqRef.current) return;
      setMe(meR.data);
      // Iter 465 — Hanya overwrite `jobs` kalau response benar-benar berisi
      //   array `items`. Response kosong/malformed TIDAK boleh menghapus
      //   cached data terakhir.
      const items = jR?.data?.items;
      if (Array.isArray(items)) {
        {
          const prev = jobsRef.current;
          // Jejak: kartu aktif yang keluar dari antrean saat refresh diberi tahu alasannya (tidak hilang diam-diam).
          const pkey = JSON.stringify({ ...params, limit: 0 });
          const sameScope = lastScopeRef.current === pkey;
          lastScopeRef.current = pkey;
          if (sameScope && prev.length) {
            const byId = new Map(items.map(j => [j.id, j]));
            const gone = prev.filter(isActiveJob).map(j => [j, byId.get(j.id)]).filter(([, n]) => !n || !isActiveJob(n));
            gone.slice(0, 3).forEach(([o, n]) => toast.info(`${o.order_number} keluar dari antrean aktif`, {
              description: !n ? "Tidak lagi tercatat atas nama Anda — hubungi Supervisor."
                : isCancelledJob(n) ? "Dibatalkan — lihat Riwayat › Batal."
                : isWarehouseForwarded(n) ? "Sudah diteruskan ke Gudang — lihat Riwayat."
                : `Dialihkan ke ${n.pic_designer_name || "desainer lain"} — lihat Riwayat.` }));
          }
        }
        setJobs(items);
        setHistTruncated(!!jR.data.history_truncated);
        if (items.length === 0 && !silent) {
          // eslint-disable-next-line no-console
          console.info("[shopee] /shopee returned 0 items (scope PIC & filter: " + JSON.stringify(params) + ")");
        }
      } else {
        setErr("Respons daftar pekerjaan tidak valid. Data terakhir dipertahankan.");
      }
      const designers = dR?.data?.designers;
      if (Array.isArray(designers)) setDesigners(designers);
      // Iter 468 — Fetch sukses: clear error ringan agar banner hilang otomatis.
      if (!silent) setErr("");
      else setErr(prev => prev && prev.startsWith("Auto-refresh") ? "" : prev);
    } catch (e) {
      // Iter 465/468 — Fetch gagal: pertahankan cached data. Error silent
      //   ditampilkan sebagai banner ringan "Auto-refresh gagal …" untuk
      //   membedakan dari error load manual.
      if (seq !== reqSeqRef.current) return;
      const detail = e?.response?.data?.detail || "Gagal memuat";
      setErr(`${silent ? "Auto-refresh gagal" : "Gagal memuat"}: ${typeof detail === "string" ? detail : "coba lagi"} — data terakhir dipertahankan.`);
    } finally {
      if (seq === reqSeqRef.current) {
        inflightRef.current = false;
        setRefreshing(false); setLoading(false);
      }
    }
  };

  // Iter 287ab — refetch backend hanya jika filter selain `status` berubah.
  // Klik KPI (yang hanya mengubah `filters.status`) TIDAK memicu fetch ulang.
  useEffect(() => { loadJobs(); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters.q, filters.designer_id, filters.job_type, filters.date_from, filters.date_to]);

  // Iter 468 — Auto-refresh 30 detik untuk Mockup Shopee.
  //   • Pause saat tab hidden (useAutoRefresh pakai Page Visibility API).
  //   • Pause saat form dirty (dirty guard global).
  //   • Mode SILENT: tidak toggle `loading`, tidak reset scroll/filter.
  //   • Inflight guard di `loadJobs` mencegah duplicate request.
  //   • Fetch gagal: data terakhir dipertahankan (handled di loadJobs).
  useAutoRefresh({ enabled: true, intervalMs: 30000, onRefresh: () => loadJobs({ silent: true }) });

  // Iter 468 — Saat tab/browser kembali aktif setelah hidden, langsung
  //   fetch (useAutoRefresh hanya re-arm interval, tidak fetch instan).
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") {
        loadJobs({ silent: true });
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSupervisor = me?.level === "supervisor";

  // Iter 287ab/396 — KPI harus memakai dataset yang SAMA dengan list yg terlihat
  // (`visibleJobs` sudah menyaring keluar pekerjaan yang sudah diteruskan ke
  // Gudang: `sent` + `completed`). Sebelum ini `counts` menghitung dari `jobs`
  // penuh sehingga Total/Fix lebih besar dari jumlah kartu — mismatch counter.
  //
  // Scope final (konsisten dengan `visibleJobs` base, SEBELUM filter status):
  //   • PIC: sudah di-scope di backend (level=designer → pic_designer_id)
  //   • dedup: `jobs` adalah list unik per `id` dari koleksi shopee_jobs
  //   • exclude: warehouse_state ∈ {"sent","completed"} → hanya tampil di Riwayat
  //
  // Catatan: chip TIDAK ikut diubah oleh pilihan filter status (sesuai perilaku
  // existing — chip adalah pintasan filter, bukan sub-filter).
  const counts = useMemo(() => {
    const active = jobs.filter(j => isActiveJob(j) && !j.is_test);
    const c = { total: active.length, BELUM_FIX: 0, REVISI: 0, FIX: 0, BATAL: jobs.filter(j => !j.transferred_from_me && isCancelledJob(j)).length };
    active.forEach(j => { if (j.status !== "BATAL" && c[j.status] != null) c[j.status]++; });
    // Iter 465 — Diagnostik: deteksi edge-case "fetch sukses dgn N > 0 tapi
    //   semua ter-forward ke Gudang → counter 0". Membantu bedakan antara
    //   "memang kosong" vs "semua sudah selesai di Gudang".
    if (jobs.length > 0 && active.length === 0) {
      // eslint-disable-next-line no-console
      console.info(`[shopee] ${jobs.length} job(s) returned by API tapi semua warehouse_state ∈ {sent, completed} → tampil 0 di antrean aktif. Buka Riwayat untuk melihatnya.`);
    }
    return c;
  }, [jobs]);

  // Daftar kartu = jobs yang match filter status (kalau ada) + urutan prioritas.
  const visibleJobs = useMemo(() => {
    // Iter 394/395 — Pekerjaan yang sudah diteruskan ke Gudang (sent/completed)
    // TIDAK boleh tampil di antrean aktif Mockup. Kartu segera lepas setelah
    // response backend sukses (state=sent) karena `patchJob` memperbarui `jobs`
    // dan filter ini otomatis re-run. Akses selanjutnya via tab Riwayat.
    const base = jobs.filter(isActiveJob).filter(j => !onlyOver24 || isOver24(j));
    let list = filters.status ? base.filter(j => j.status === filters.status) : base.slice();
    // Iter 287aj — Urutan prioritas client-side.
    const ts = (j) => Date.parse(j.created_at || 0) || 0;
    if (sortBy === "priority") {
      // 1) batas kirim Shopee <24 jam / lewat · 2) lewat 24 jam sejak rekap belum ke Gudang · 3) lainnya.
      const dl = (j) => (["shipped", "completed", "to_return"].includes(j.shopee_order_status) ? null : deadlineMs(j.shopee_deadline_iso)) ?? Infinity;
      const now = Date.now();
      const rank = (j) => dl(j) - now < 24 * 3600000 ? 0 : isOver24(j) ? 1 : 2;
      list.sort((a, b) => rank(a) - rank(b) || dl(a) - dl(b) || ts(a) - ts(b));
    } else if (sortBy === "recent" || !sortBy) {
      list.sort(byRecentClaim);
    } else if (sortBy === "todo") {
      // Belum FIX/Revisi dulu, lalu FIX, lalu BATAL; dalam grup: terbaru dulu.
      const rank = (j) => j.status === "BELUM_FIX" ? 0
        : j.status === "REVISI" ? 1
        : j.status === "FIX" ? 2 : 3;
      list.sort((a, b) => rank(a) - rank(b) || ts(b) - ts(a));
    } else if (sortBy === "deadline") {
      const active = (j) => (j.status === "BELUM_FIX" || j.status === "REVISI") ? 0 : 1;
      const dl = (j) => (["shipped", "completed", "to_return"].includes(j.shopee_order_status) ? null : deadlineMs(j.shopee_deadline_iso)) ?? Infinity;
      list.sort((a, b) => active(a) - active(b) || dl(a) - dl(b) || ts(b) - ts(a));
    } else if (sortBy === "waiting") {
      // Menunggu Gudang: sent+queued+FIX dulu, lalu sisanya; grup: terbaru dulu.
      const rank = (j) => {
        const proc = j.warehouse_process_state || "queued";
        if (j.warehouse_state === "sent" && proc !== "processing" && j.status === "FIX"
          && !(j.pending_request && j.pending_request.status === "pending")) return 0;
        if (j.warehouse_state === "sent" && j.status === "FIX") return 1;
        return 2;
      };
      list.sort((a, b) => rank(a) - rank(b) || ts(b) - ts(a));
    } else if (sortBy === "hold") {
      // Ditahan / pengajuan menunggu dulu; lalu sisanya; grup: terbaru dulu.
      const rank = (j) => {
        if (j.pending_request && j.pending_request.status === "pending") return 0;
        if (j.warehouse_state === "on_hold") return 1;
        return 2;
      };
      list.sort((a, b) => rank(a) - rank(b) || ts(b) - ts(a));
    }
    // Pekerjaan UJI selalu di bawah pekerjaan nyata.
    return list.filter(j => !j.is_test).concat(list.filter(j => j.is_test));
  }, [jobs, filters.status, sortBy, onlyOver24]);
  const aiReads = useAiFileReads(visibleJobs);

  const over24Count = useMemo(() => jobs.filter(isOver24).length, [jobs]);

  return <div className="shopee-shell shopee-theme" data-testid="shopee-module" style={{padding: embedded ? 0 : 16}}>
    <style>{`
      /* Standar warna: tombol mengikuti warna TAMFARIS; oranye Shopee hanya untuk badge identitas. */
      .shopee-theme .shopee-badge { background:var(--tf-shopee) !important; color:#fff !important; padding:3px 8px; border-radius:6px; font-size:11px; font-weight:700; letter-spacing:0.5px; }
    `}</style>
    <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, marginBottom:12, flexWrap:"wrap"}}>
      <div style={{display:"flex", alignItems:"center", gap:10}}>
        {onBack && <button className="soft-btn small-btn" onClick={onBack} data-testid="shopee-back-btn"><ArrowLeft size={14}/> Kembali</button>}
        <div>
          <div style={{fontSize:11, letterSpacing:1, textTransform:"uppercase", color:"#94a3b8", fontWeight:700}}>OPERASIONAL</div>
          <h2 style={{margin:0, fontSize:22, fontWeight:800, letterSpacing:"-0.02em"}}>Mockup Shopee</h2>
          {me && <div className="muted small">
            {me.level === "supervisor" ? `Supervisor · ${me.name}` : `Designer · ${me.name}`}
          </div>}
        </div>
      </div>
    </div>
    {/* Iter 628 — Dashboard Performa Desainer tepat di bawah identitas (dipindah, bukan duplikat). */}
    <div className="shopee-stats-panel shopee-stats-top" data-testid="shopee-stats-panel" style={{marginTop:0, marginBottom:12, padding:0, minWidth:0, overflowX:"auto"}}>
      <LeaderboardMockupShopee testIdPrefix="shopee-mockup-leaderboard" compact meId={me?.id} meName={me?.name}/>
    </div>
    <div style={{display:"flex", gap:8, flexWrap:"wrap", marginBottom:12}} data-testid="shopee-action-bar">
        {/* Iter 358 — Tombol Riwayat Pekerjaan: panel read-only semua pekerjaan
            PIC (termasuk BATAL/FIX/dikirim ke Gudang) mengikuti pola Riwayat Rido. */}
        <button className="soft-btn small-btn" onClick={()=>openRiwayat("")} data-testid="shopee-riwayat-btn"><History size={14}/> Riwayat</button>
        <button className="soft-btn small-btn" onClick={()=>setShowRekap(true)} data-testid="shopee-rekap-btn"><Layers size={14}/> Rekap</button>
        <button className="soft-btn small-btn" onClick={() => loadJobs()} data-testid="shopee-refresh-btn" title="Perbarui data sekarang">
          <RefreshCw size={14} className={refreshing || loading ? "spin" : ""}/>
          {refreshing && !loading ? "Memuat ulang…" : "Muat Ulang"}
        </button>
        <button className="primary small-btn" onClick={()=>setShowForm(true)} data-testid="shopee-new-btn"><Plus size={14}/> Buat Pekerjaan</button>
    </div>

    {/* Iter 558 — Antrean Shopee API (Belum Diambil / Sudah Ada di Sistem).
        Satu-klik "Ambil Pekerjaan" → atomic claim → auto-create shopee_jobs →
        buka JobDetail existing utk designer melengkapi process/jenis desain. */}
    <div className="shopee-workspace" data-testid="shopee-workspace">
    <aside className="shopee-ws-queue" data-testid="shopee-ws-queue">
    <h3 className="shopee-ws-title">Ambil Pesanan Baru</h3>
    <ShopeeMockupQueuePanel
      me={me}
      defaultOpenDesktop
      onJobCreated={(job) => { setJobs(js => [job, ...js.filter(x => x.id !== job.id)]); loadJobs(); setDetailId(job.id); }}
      onOpenExisting={(jobId) => setDetailId(jobId)}
    />
    </aside>
    <section className="shopee-ws-jobs" data-testid="shopee-ws-jobs">
    <h3 className="shopee-ws-title">Pekerjaan Saya</h3>

    {/* Summary chips — Iter 287u: klik = filter status. Total = kosongkan filter. */}
    <div className="shp-stats" data-testid="shopee-stats-panel">
      <div className="shp-stats-row" role="group" aria-label="Ringkasan pekerjaan aktif">
        {[
          {k:"total", l:"Total Aktif", v:counts.total, tone:"neutral"},
          {k:"BELUM_FIX", l:"Belum FIX", v:counts.BELUM_FIX, tone:"neutral"},
          {k:"REVISI", l:"Revisi", v:counts.REVISI, tone:"revisi"},
          {k:"FIX", l:"FIX Belum ke Gudang", v:counts.FIX, tone:"neutral"},
        ].map(t => {
          const isActive = !onlyOver24 && (t.k === "total" ? !filters.status : filters.status === t.k);
          return <button key={t.k} type="button" className={`shp-stat is-${t.tone}${isActive ? " is-on" : ""}${t.v ? "" : " is-zero"}`}
            onClick={() => { setOnlyOver24(false); setFilters(f => ({...f, status: t.k === "total" ? "" : t.k})); }}
            aria-pressed={isActive} data-testid={`shopee-stat-${t.k.toLowerCase()}`}>
            <b>{t.v}</b><span>{t.l}</span>
          </button>;
        })}
      </div>
      <button type="button" className={`shp-over24${over24Count ? " is-hot" : ""}${onlyOver24 ? " is-on" : ""}`}
        onClick={() => { setOnlyOver24(v => !v); setFilters(f => ({ ...f, status: "" })); }}
        aria-pressed={onlyOver24} data-testid="shopee-stat-over24">
        <Clock size={13}/> Belum ke Gudang &gt;24 Jam: <b>{over24Count}</b>
        <small>bagian dari pekerjaan aktif</small>
      </button>
    </div>

    {/* Iter 544 — Leaderboard produktivitas Operator Mockup Shopee.
        Tempat: setelah ringkasan status KPI, sebelum filter — menonjol tapi
        tidak membuat halaman panjang (collapsible by default via tinggi tetap chart). */}
    {/* Filters */}
    <div className="card" style={{padding:10, marginBottom:12, display:"flex", flexWrap:"wrap", gap:8}}>
      <div style={{display:"flex", alignItems:"center", gap:6, flex:"1 1 220px", minWidth:0}}>
        <Search size={14}/>
        <input type="text" placeholder="Cari nomor pesanan, akun, atau nama file…"
          value={filters.q} onChange={e=>setFilters(f=>({...f, q:e.target.value}))}
          style={{flex:1, minWidth:0, padding:"6px 8px", border:"1px solid #e2e8f0", borderRadius:6}}
          data-testid="shopee-search-input"/>
      </div>
      <select value={filters.status} onChange={e=>setFilters(f=>({...f, status:e.target.value}))}
        style={{padding:"6px 8px", border:"1px solid #e2e8f0", borderRadius:6}}
        data-testid="shopee-filter-status">
        <option value="">Semua status</option>
        <option value="BELUM_FIX">Belum Fix</option>
        <option value="REVISI">Revisi</option>
        <option value="FIX">Fix</option>
      </select>
      <select value={filters.job_type} onChange={e=>setFilters(f=>({...f, job_type:e.target.value}))}
        style={{padding:"6px 8px", border:"1px solid #e2e8f0", borderRadius:6}}
        data-testid="shopee-filter-jobtype">
        <option value="">Reguler + Lembur</option>
        <option value="reguler">Reguler</option>
        <option value="lembur">Lembur</option>
      </select>
      {isSupervisor && <select value={filters.designer_id} onChange={e=>setFilters(f=>({...f, designer_id:e.target.value}))}
        style={{padding:"6px 8px", border:"1px solid #e2e8f0", borderRadius:6}}
        data-testid="shopee-filter-designer">
        <option value="">Semua desainer</option>
        {designers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
      </select>}
      {/* Iter 287aj — Urutan prioritas kartu. */}
      <select value={sortBy} onChange={e=>setSortBy(e.target.value)}
        style={{padding:"6px 8px", border:"1px solid #e2e8f0", borderRadius:6}}
        title="Urutan tampilan kartu"
        data-testid="shopee-filter-sort">
        <option value="priority">Prioritas (mendesak & &gt;24 jam dulu)</option>
        <option value="recent">↓ Terbaru diambil</option>
        <option value="todo">📝 Belum FIX dulu</option>
        <option value="deadline">⏰ Batas kirim terdekat</option>
        <option value="waiting">⏳ Menunggu Gudang</option>
        <option value="hold">⏸ Ditahan / Pengajuan</option>
      </select>
      <div className="shopee-view-toggle" role="group" aria-label="Mode tampilan" data-testid="shopee-view-toggle">
        {[["card", "Kartu"], ["list", "Daftar Padat"]].map(([k, l]) => <button key={k} type="button" aria-pressed={viewMode === k}
          className={viewMode === k ? "is-on" : ""} onClick={() => setViewModePersist(k)} data-testid={`shopee-view-${k}`}>{l}</button>)}
      </div>
      <div className="shopee-date-grid">
        <label className="shopee-date-field">
          <span>Dari tanggal</span>
          <input type="date" value={filters.date_from} onChange={e=>setFilters(f=>({...f, date_from:e.target.value}))}
            data-testid="shopee-filter-datefrom"/>
        </label>
        <label className="shopee-date-field">
          <span>Sampai tanggal</span>
          <input type="date" value={filters.date_to} onChange={e=>setFilters(f=>({...f, date_to:e.target.value}))}
            data-testid="shopee-filter-dateto"/>
        </label>
      </div>
    </div>

    {err && <div className="warn-box small" data-testid="shopee-error">{err}</div>}
    {loading && <div className="muted small"><Loader2 size={14} className="spin"/> Memuat…</div>}
    {!loading && visibleJobs.length === 0 && !err && <div className="muted small" data-testid="shopee-empty">
      {jobs.length > 0 ? <>
        Tidak ada pekerjaan aktif. Semua pekerjaan sudah dikirim/selesai di Gudang.{" "}
        <button type="button" className="link-btn" onClick={()=>openRiwayat("")} data-testid="shopee-empty-open-riwayat">Lihat Riwayat Pekerjaan</button>.
      </> : "Belum ada pekerjaan."}
    </div>}

    <div className={`shopee-card-grid${viewMode === "list" ? " is-list" : ""}`} data-view={viewMode} style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))", gap:12}}>
      {visibleJobs.map(j => <JobCard key={j.id} job={j} isSupervisor={isSupervisor} aiReads={aiReads}
        onOpen={()=>setDetailId(j.id)} onPatched={patchJob}
        onOpenPreview={(job)=>setPreviewForJob(job)}
        onQuickAction={(action, job, payload)=>handleQuickAction(action, job, payload)}
        busyId={busyJobId === j.id ? busyKind : null}
        onSendWarehouse={async (job) => {
          // Iter 395 — Guard anti double-click: tolak eksekusi kedua selama
          // modal konfirmasi dibuka ATAU request sedang berjalan.
          if (sendingRef.current.has(job.id)) return;
          // Iter 287ae — Validasi Proses Pengerjaan sebelum konfirmasi.
          if (!(job.process || "").trim()) {
            notify.warning({ title: "Pilih Proses Pengerjaan", message: "Set 'Proses Pengerjaan' terlebih dahulu (buka detail pekerjaan)." });
            return;
          }
          sendingRef.current.add(job.id);
          try {
            // Iter 287p — Ganti window.confirm dgn notify.confirm (modal in-app oranye).
            const ok = await notify.confirm({
              title: "Kirim ke Gudang?",
              message: `Mockup pesanan ${job.order_number} akan dikirim ke Gudang. Setelah dikirim, pekerjaan terkunci dan tidak bisa diedit.`,
              primaryLabel: "Kirim Sekarang",
              secondaryLabel: "Batal",
            });
            if (!ok) return;
            try {
              setBusyJobId(job.id); setBusyKind("send");
              // Iter 287ai — pakai respons langsung; state auto-refresh via patchJob.
              const r = await axios.post(`${API}/shopee/${job.id}/send-to-warehouse`);
              if (r?.data) patchJob(r.data);
              notify.success({ title: "Berhasil", message: "Mockup berhasil dikirim ke Gudang." });
            } catch (e) {
              notify.error({ title: "Gagal kirim ke Gudang", message: formatApiError(e, "Gagal kirim ke Gudang.") });
            } finally { setBusyJobId(null); setBusyKind(""); }
          } finally { sendingRef.current.delete(job.id); }
        }}/>)}
    </div>
    </section>
    </div>

    {showForm && <JobForm me={me} designers={designers} onClose={()=>setShowForm(false)}
      onSaved={(j)=>{ setShowForm(false); loadJobs(); setDetailId(j.id); }}
      onDuplicateFound={(j)=>{ setShowForm(false); setDetailId(j.id); }}/>}
    {editJob && <EditJobForm job={editJob} me={me} designers={designers}
      onClose={()=>setEditJob(null)}
      onSaved={(j)=>{ setEditJob(null); if (j && j.id) patchJob(j); }}/>}
    {detailId && <JobDetail jobId={detailId} me={me} designers={designers}
      onClose={()=>{ setDetailId(null); }}
      onUpdated={(j)=>{ if (j && j.id) patchJob(j); }}/>}
    {showRekap && <RekapPanel filters={filters} isSupervisor={isSupervisor}
      onClose={()=>setShowRekap(false)}/>}
    {/* Iter 358/359 — Panel Riwayat Pekerjaan (reusable utk Shopee + Mockup Offline).
        Normalisasi shopee_jobs → bentuk item universal RiwayatPekerjaanModal. */}
    {showRiwayat && <RiwayatPekerjaanModal
      initialStatus={riwayatStatus}
      flavor="shopee"
      testPrefix="shopee-riwayat"
      title="Riwayat Pekerjaan"
      me={me}
      canFilterByPic={isSupervisor}
      pics={(designers || []).map(d => ({ id: d.id, name: d.name }))}
      items={(jobs || [])
        // Iter 469 — Defensive client-side scope: designer HANYA lihat jobs
        //   yang pic_designer_id-nya sama persis dengan dirinya. Backend
        //   sudah scope (filt["pic_designer_id"]=_actor_id), tapi safety-net
        //   ini mencegah record "nyasar" (initial_pic berbeda, legacy, dsb.)
        //   ikut tampil kalau cache lama atau race condition fetch terjadi.
        .filter(j => isSupervisor || !me?.id || j?.pic_designer_id === me.id || j?.transferred_from_me)
        .map(j => {
        const mockups = (j.mockups || []).filter(m => !m.deleted_at);
        const revisi = mockups.reduce((n, m) => n + Math.max(0, (m.versions || []).length - 1), 0);
        // Iter 470 — Derive thumb URL dari mockup aktif pertama yang image (reuse
        //   pola `JobCard`). Null bila tidak ada → RiwayatCard tampilkan placeholder.
        // Iter 499 — Derive juga `previewUrl` + `previewFilename` + `previewIsPdf`
        //   dari file aktif yang sama supaya thumbnail di Riwayat clickable untuk
        //   buka preview, termasuk setelah job FIX/forwarded ke Gudang. Backend
        //   `/shopee/files/{id}/preview` sudah mengizinkan designer (PIC job) view
        //   file miliknya tanpa restriksi state → tidak perlu ubah backend.
        let thumbUrl = null;
        let previewUrl = null;
        let previewFilename = "";
        let previewIsPdf = false;
        // Iter 514 — Build FULL list `mockup_previews` untuk semua mockup aktif
        //   (bukan hanya 1 file terakhir). Riwayat preview jadi gallery supaya
        //   job dengan 2+ mockup tetap bisa dilihat lengkap. Tidak sentuh
        //   thumb_url / preview_url (backward compatible dengan RiwayatCard lama).
        const mockupPreviews = [];
        for (const m of mockups) {
          const v = m.versions?.[m.current_version - 1] || m.versions?.[m.versions.length - 1];
          if (!v?.file_id) continue;
          mockupPreviews.push({
            file_id: v.file_id,
            filename: v.filename || "mockup",
            title: m.title || v.filename || "mockup",
            content_type: v.content_type || "",
            version: v.version,
            is_pdf: (v.content_type || "") === "application/pdf",
            preview_url: `${API}/shopee/files/${v.file_id}/preview`,
            thumb_url: (v.content_type || "").startsWith("image")
              ? `${API}/shopee/files/${v.file_id}/thumbnail?size=400`
              : null,
          });
        }
        for (const m of mockups.slice().reverse()) {
          const v = m.versions?.[m.current_version - 1] || m.versions?.[m.versions.length - 1];
          if (v?.file_id && (v.content_type || "").startsWith("image")) {
            thumbUrl = `${API}/shopee/files/${v.file_id}/thumbnail?size=400`;
            previewUrl = `${API}/shopee/files/${v.file_id}/preview`;
            previewFilename = v.filename || "mockup";
            previewIsPdf = (v.content_type || "") === "application/pdf";
            break;
          }
        }
        // Fallback: ada file tapi non-image (mis. PDF) — tetap sediakan preview.
        if (!previewUrl) {
          for (const m of mockups.slice().reverse()) {
            const v = m.versions?.[m.current_version - 1] || m.versions?.[m.versions.length - 1];
            if (v?.file_id) {
              previewUrl = `${API}/shopee/files/${v.file_id}/preview`;
              previewFilename = v.filename || "mockup";
              previewIsPdf = (v.content_type || "") === "application/pdf";
              break;
            }
          }
        }
        const procLabel = ({
          bordir: "Bordir", sablon_dtf: "Sablon DTF",
          bordir_sablon_dtf: "Bordir + Sablon DTF", sublim_full_print: "Sublim/Full Print",
        })[j.process] || "";
        const jobLabel = [j.design_title, procLabel].filter(Boolean).join(" · ") || "—";
        const ws = j.warehouse_state;
        const next = ws === "completed" ? { label: "Selesai di Gudang", cls: "riw-chip-success" }
          : ws === "sent" ? { label: "Dikirim ke Gudang", cls: "riw-chip-success" }
          : ws === "cancelled" ? { label: "Pengiriman dibatalkan", cls: "riw-chip-neutral" }
          : ws === "recalled" ? { label: "Ditarik kembali", cls: "riw-chip-warn" }
          : { label: "Belum dikirim", cls: "riw-chip-neutral" };
        return {
          id: j.id,
          order_number: j.order_number,
          customer: j.customer_account || "",
          pic_name: j.pic_designer_name || "",
          pic_id: j.pic_designer_id || "",
          job_label: jobLabel,
          status: isCancelledJob(j) ? "BATAL" : (j.status || "BELUM_FIX"),
          created_at: j.created_at,
          updated_at: j.updated_at || j.created_at,
          revision_count: revisi,
          is_fix: j.status === "FIX",
          next_label: next.label,
          next_cls: next.cls,
          is_cancelled: isCancelledJob(j),
          cancel_reason: j.cancel_reason || j.canceled_internal?.reason || j.canceled_internal?.original_reason
            || (SHOPEE_CANCELLED.has(j.shopee_order_status) ? "Dibatalkan di Shopee" : ""),
          extra_label: j.transferred_from_me ? `Dialihkan ke ${j.pic_designer_name || "desainer lain"}` : (j.job_type === "lembur" ? "Lembur" : ""),
          fix_at: j.first_fix_at || "",
          sent_at: j.warehouse_sent_at || j.warehouse_first_sent_at || "",
          thumb_url: thumbUrl,
          preview_url: previewUrl,
          preview_filename: previewFilename,
          preview_is_pdf: previewIsPdf,
          // Iter 514 — list lengkap semua mockup aktif → RiwayatCard pakai
          //   gallery navigasi bila length > 1. Backward compatible: field
          //   lama (thumb_url/preview_url) tetap diisi.
          mockup_previews: mockupPreviews,
          // Iter 541 — Status HOLD/BATAL dari shopee_jobs untuk kartu Riwayat.
          hold_active: Boolean((j.hold || {}).active),
          cancellation_status: j.cancellation_status || "",
          _raw: j,
        };
      })}
      onOpen={(it)=>{ setShowRiwayat(false); setDetailId(it.id); }}
      onHoldDone={()=>loadJobs()}
      onClose={()=>setShowRiwayat(false)}
    />}
    {/* Iter 287w — Modal quick actions kartu */}
    <input ref={quickFileRef} type="file" accept="image/*,application/pdf" hidden
      onChange={e => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f || !quickFileJob) return;
        setQuickPaste({ job: quickFileJob, preview: { file: f, url: URL.createObjectURL(f), source: "pick" } });
        setQuickFileJob(null);
      }}
      data-testid="shopee-quick-add-input"/>
    {quickAddJob && <QuickAddModal job={quickAddJob} onClose={()=>setQuickAddJob(null)}
      onPickFile={()=>{ setQuickFileJob(quickAddJob); setQuickAddJob(null); quickFileRef.current?.click(); }}
      onImage={(file)=>{ setQuickPaste({ job: quickAddJob, preview: { file, url: URL.createObjectURL(file), source: "paste" } }); setQuickAddJob(null); }}/>}
    {quickPaste && <QuickPasteHost jobId={quickPaste.job.id} preview={quickPaste.preview} defaultFormat={jobPaper(quickPaste.job)}
      mockups={(quickPaste.job.mockups || []).filter(m => !m.deleted_at)}
      onClose={()=>setQuickPaste(null)}
      onDone={async ()=>{ setQuickPaste(null); await loadJobs(); }}/>}
    {previewForJob && <QuickPreviewModal job={previewForJob} onClose={()=>setPreviewForJob(null)}/>}
    {quickReqHold && <PendingRequestDialog kind={quickReqHold.kind}
      onClose={()=>setQuickReqHold(null)}
      onConfirm={async (reason, details) => {
        try {
          const r = await axios.post(`${API}/shopee/${quickReqHold.job.id}/pending-request`, {
            kind: quickReqHold.kind, reason, details,
          });
          patchJob(r.data);
          setQuickReqHold(null);
          notify.success({ title: "Pengajuan terkirim", message: "Menunggu keputusan Gudang." });
        } catch (e) {
          notify.error({ title: "Gagal", message: e?.response?.data?.detail || e.message });
          throw e;
        }
      }}/>}
    {quickCancel && <CancelDialog onClose={()=>setQuickCancel(null)}
      onConfirm={async (reason) => {
        // Iter 287al — Payload fix: `status` (bukan `new`) + error normalization.
        // Endpoint: POST /api/shopee/{id}/status body: { status, reason }.
        // Aturan lock backend (`_guard_not_locked`) tetap: setelah processing,
        // status BATAL langsung ditolak — user harus lewat pengajuan Ajukan Batal.
        try {
          const r = await axios.post(`${API}/shopee/${quickCancel.id}/status`, { status: "BATAL", reason });
          patchJob(r.data);
          setQuickCancel(null);
          notify.success({ title: "Pekerjaan dibatalkan", message: quickCancel.order_number });
        } catch (e) {
          notify.error({ title: "Gagal membatalkan", message: formatApiError(e, "Gagal membatalkan pesanan.") });
          throw e;  // biar CancelDialog reset busy tapi tetap buka + pertahankan alasan
        }
      }}/>}
  </div>;
}


// Iter 287w — Host modal paste untuk quick add/tempel dari kartu (tanpa buka detail).
// Gambar pertama dari ClipboardEvent / navigator.clipboard (null bila tidak ada).
export const clipboardEventImage = (e) => {
  for (const it of e.clipboardData?.items || []) if (it.kind === "file" && (it.type || "").startsWith("image/")) { const f = it.getAsFile(); if (f) return f; }
  return null;
};
async function readClipboardFile() {
  if (!navigator.clipboard?.read) throw Object.assign(new Error("unsupported"), { name: "Unsupported" });
  for (const it of await navigator.clipboard.read()) {
    const type = (it.types || []).find(t => t.startsWith("image/"));
    if (type) return new File([await it.getType(type)], `clipboard-${Date.now()}.${type.split("/")[1] || "png"}`, { type });
  }
  return null;
}
const clipErrText = (e) => e?.name === "Unsupported" ? "Browser tidak mendukung tempel gambar. Gunakan Pilih File."
  : e?.name === "NotAllowedError" ? "Izin clipboard ditolak. Tekan Ctrl+V / ⌘+V, atau gunakan Pilih File."
  : "Gagal membaca clipboard. Gunakan Pilih File.";

// Tambah Mockup dari kartu: Ctrl+V / ⌘+V atau Tempel dari Clipboard → pratinjau; Pilih File tetap tersedia.
function QuickAddModal({ job, onClose, onPickFile, onImage }) {
  const [err, setErr] = useState("");
  const doneRef = useRef(false);
  const take = (f) => { if (!f || doneRef.current) return; doneRef.current = true; onImage(f); };
  useEffect(() => {
    const onPaste = (e) => { const f = clipboardEventImage(e); if (!f) { setErr("Clipboard tidak berisi gambar. Salin gambar dulu, lalu tempel."); return; } e.preventDefault(); take(f); };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  });
  const fromClipboard = async () => {
    setErr("");
    try { const f = await readClipboardFile(); if (f) take(f); else setErr("Clipboard tidak berisi gambar. Salin gambar dulu, lalu tempel."); }
    catch (e) { setErr(clipErrText(e)); }
  };
  return <ModalShell title={`Tambah Mockup — ${job.order_number}`} onClose={onClose} testid="shopee-quick-add-modal">
    <div className="shp-paste-zone" tabIndex={0} data-testid="shopee-quick-add-zone">
      <ClipboardPaste size={28} aria-hidden="true"/>
      <b>Tekan Ctrl+V / ⌘+V untuk menempel gambar</b>
      <span className="muted small">Pratinjau muncul sebelum disimpan. Status tidak berubah menjadi FIX.</span>
    </div>
    {err && <div className="warn-box small" style={{marginTop:8}} data-testid="shopee-quick-add-error">{err}</div>}
    <div className="shp-modal-actions" style={{display:"flex", gap:8, justifyContent:"flex-end", marginTop:6}}>
      <button className="soft-btn small-btn" onClick={fromClipboard} data-testid="shopee-quick-add-clipboard"><ClipboardPaste size={14}/> Tempel dari Clipboard</button>
      <button className="primary small-btn" onClick={onPickFile} data-testid="shopee-quick-add-pick"><Upload size={14}/> Pilih File</button>
    </div>
  </ModalShell>;
}

function QuickPasteHost({ jobId, preview, mockups, onClose, onDone, defaultFormat = "A5L" }) {
  const [uploading, setUploading] = useState(false);
  const uploadNew = async (title, fmt = "A5L", g = {}) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", preview.file);
      const params = new URLSearchParams({ paper_format: fmt, ...g }); if (title) params.set("title", title);
      showOrderNoCheck(await axios.post(`${API}/shopee/${jobId}/mockups?${params.toString()}`, fd, { headers: {"Content-Type":"multipart/form-data"} }));
      await onDone();
    } catch (e) {
      notify.error({ title: "Gagal upload", message: e?.response?.data?.detail || e.message });
    } finally { setUploading(false); }
  };
  const uploadVersion = async (mockupId, note, fmt = "A5L", g = {}) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", preview.file);
      const params = new URLSearchParams({ paper_format: fmt, ...g }); if (note) params.set("note", note);
      showOrderNoCheck(await axios.post(`${API}/shopee/${jobId}/mockups/${mockupId}/versions?${params.toString()}`, fd, { headers: {"Content-Type":"multipart/form-data"} }));
      await onDone();
    } catch (e) {
      notify.error({ title: "Gagal upload versi", message: e?.response?.data?.detail || e.message });
    } finally { setUploading(false); }
  };
  return <PastePreviewModal preview={preview} mockups={mockups} uploading={uploading} defaultFormat={defaultFormat}
    precheckUrl={`${API}/shopee/${jobId}/mockups/precheck`}
    onCancel={onClose} onSaveNew={uploadNew} onSaveVersion={uploadVersion}/>;
}


// Iter 287w — Preview thumbnail cepat (mockup pertama) tanpa buka detail.
function QuickPreviewModal({ job, onClose }) {
  // Iter 514 — GALLERY untuk SEMUA mockup aktif (bukan single image).
  //   Audit sebelumnya: `.slice(-1)[0]` hanya mengambil 1 image terakhir →
  //   jika job punya 2+ mockup, hanya 1 yang terlihat. Perbaikan: list semua
  //   mockup aktif dengan versi aktif, lalu tampilkan sebagai gallery dengan
  //   navigasi prev/next + counter "N / M".
  const items = useMemo(() => {
    const active = (job.mockups || []).filter(m => !m.deleted_at);
    const list = [];
    for (const m of active) {
      const v = m.versions?.[m.current_version - 1] || m.versions?.[m.versions.length - 1];
      if (!v?.file_id) continue;
      list.push({
        file_id: v.file_id,
        filename: v.filename || "mockup",
        title: m.title || v.filename || "mockup",
        content_type: v.content_type || "",
        version: v.version,
        is_pdf: (v.content_type || "") === "application/pdf",
      });
    }
    return list;
  }, [job]);
  const [idx, setIdx] = useState(0);
  const total = items.length;
  const cur = items[Math.min(idx, Math.max(0, total - 1))];
  useEffect(() => {
    if (total === 0) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") setIdx(i => (i - 1 + total) % total);
      else if (e.key === "ArrowRight") setIdx(i => (i + 1) % total);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [total]);
  if (!cur) {
    return <ModalShell title={`Pratinjau — ${job.order_number}`} onClose={onClose} testid="shopee-quick-preview">
      <div className="muted small">Belum ada mockup untuk pratinjau.</div>
    </ModalShell>;
  }
  const prev = () => setIdx(i => (i - 1 + total) % total);
  const next = () => setIdx(i => (i + 1) % total);
  return <ModalShell title={`Pratinjau — ${job.order_number}${total > 1 ? ` · ${total} Mockup` : ""}`} onClose={onClose} testid="shopee-quick-preview" wide>
    <div style={{position:"relative", textAlign:"center", padding:8, background:"#0f141c", borderRadius:6}}
      data-testid="shopee-quick-preview-gallery">
      {cur.is_pdf
        ? <iframe src={`${API}/shopee/files/${cur.file_id}/preview`} title={cur.filename}
            style={{width:"100%", height:"70vh", border:0, background:"#fff"}}
            data-testid="shopee-quick-preview-pdf"/>
        : <img src={`${API}/shopee/files/${cur.file_id}/preview`} alt={cur.title}
            style={{maxWidth:"100%", maxHeight:"70vh", objectFit:"contain"}}
            data-testid="shopee-quick-preview-image"/>}
      {total > 1 && <>
        <button type="button" onClick={prev}
          aria-label="Mockup sebelumnya"
          data-testid="shopee-quick-preview-prev"
          style={{position:"absolute", left:12, top:"50%", transform:"translateY(-50%)",
            background:"rgba(15,23,42,.72)", color:"#fff", border:"1px solid rgba(255,255,255,.2)",
            borderRadius:"50%", width:40, height:40, fontSize:20, fontWeight:700, cursor:"pointer"}}>‹</button>
        <button type="button" onClick={next}
          aria-label="Mockup berikutnya"
          data-testid="shopee-quick-preview-next"
          style={{position:"absolute", right:12, top:"50%", transform:"translateY(-50%)",
            background:"rgba(15,23,42,.72)", color:"#fff", border:"1px solid rgba(255,255,255,.2)",
            borderRadius:"50%", width:40, height:40, fontSize:20, fontWeight:700, cursor:"pointer"}}>›</button>
        <div data-testid="shopee-quick-preview-counter"
          style={{position:"absolute", top:12, right:12, background:"rgba(15,23,42,.72)", color:"#fff",
            padding:"4px 10px", borderRadius:999, fontSize:12, fontWeight:700}}>
          {idx + 1} / {total}
        </div>
      </>}
    </div>
    {total > 1 && (
      <div style={{display:"flex", gap:6, overflowX:"auto", padding:"8px 2px", marginTop:6}}
        data-testid="shopee-quick-preview-strip">
        {items.map((it, i) => (
          <button key={it.file_id} type="button" onClick={()=>setIdx(i)}
            data-testid={`shopee-quick-preview-thumb-${i}`}
            aria-label={`Pilih mockup ${i + 1}`}
            style={{flexShrink:0, padding:0, border: i === idx ? "2px solid var(--tf-primary)" : "2px solid transparent",
              borderRadius:6, cursor:"pointer", background:"#0f141c"}}>
            {it.is_pdf
              ? <div style={{width:60, height:60, display:"flex", alignItems:"center", justifyContent:"center",
                  color:"#f1f5f9", fontSize:10, fontWeight:700}}>PDF v{it.version}</div>
              : <img src={`${API}/shopee/files/${it.file_id}/thumbnail?size=120`} alt={it.title}
                  style={{width:60, height:60, objectFit:"cover", borderRadius:4, display:"block"}}/>}
          </button>
        ))}
      </div>
    )}
    <p className="muted small" style={{marginTop:6, textAlign:"center"}}>
      <strong>{cur.title}</strong>
      {cur.filename && cur.filename !== cur.title ? <> · {cur.filename}</> : null}
      {cur.version ? <> · v{cur.version}</> : null}
    </p>
  </ModalShell>;
}


// Pilihan A5 / A4 di kartu utama. A4 mempertahankan orientasi tersimpan (Portrait default, Landscape bila sudah dipilih).
export function CardPaperToggle({ job, onChanged }) {
  const [busy, setBusy] = useState(false);
  const cur = jobPaper(job);
  const locked = job.status === "BATAL" || ["sent", "completed"].includes(job.warehouse_state);
  const pick = async (fam) => {
    const next = fam === "A5" ? "A5L" : (cur.startsWith("A4") ? cur : "A4P");
    if (busy || locked || next === cur && job.paper_pref) return;
    setBusy(true);
    try { const r = await axios.post(`${API}/shopee/${job.id}/paper-pref`, { paper_format: next }); onChanged?.(r.data); }
    catch (e) { notify.error({ title: "Gagal menyimpan ukuran kertas", message: formatApiError(e, "Coba lagi.") }); }
    finally { setBusy(false); }
  };
  return <div className="shp-paper-toggle" role="radiogroup" aria-label="Ukuran kertas" onClick={e => e.stopPropagation()}
    onKeyDown={e => e.stopPropagation()} title={locked ? "Dikunci: sudah di Gudang / dibatalkan" : FORMAT_LABEL[cur]}
    data-testid={`shopee-card-paper-${job.id}`}>
    {["A5", "A4"].map(f => <button key={f} type="button" role="radio" aria-checked={cur.startsWith(f)} disabled={busy || locked}
      className={cur.startsWith(f) ? "active" : ""} onClick={() => pick(f)} data-testid={`shopee-card-paper-${job.id}-${f.toLowerCase()}`}>
      {f}{f === "A4" && cur === "A4L" ? " L" : ""}</button>)}
  </div>;
}

function JobCard({ job, isSupervisor, onOpen, onSendWarehouse, onOpenPreview, onQuickAction, onPatched, busyId, aiReads }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const s = STATUS_LABELS[job.status] || STATUS_LABELS.BELUM_FIX;
  // Iter 287o — Hitung & tampilkan hanya mockup aktif (tidak ter-soft-delete).
  const activeMockups = (job.mockups || []).filter(m => !m.deleted_at);
  // Prefer mockup dengan versi image terbaru; kalau tidak ada, jatuh ke slot manapun.
  let thumbUrl = null;
  for (const m of activeMockups.slice().reverse()) {
    const v = m.versions?.[m.current_version - 1] || m.versions?.[m.versions.length - 1];
    if (v?.file_id && (v.content_type || "").startsWith("image")) {
      thumbUrl = `${API}/shopee/files/${v.file_id}/thumbnail?size=400`;
      break;
    }
  }
  // Iter 287g — tombol Kirim ke Gudang cepat dari kartu, hanya bila belum sent.
  // Iter 394 — Treat "completed" juga sebagai forwarded (post-ACC Gudang).
  const canSend = job.status === "FIX" && activeMockups.length > 0
    && !isWarehouseForwarded(job);
  // Iter 287ah — Label status Gudang mengikuti kombinasi status desain + state Gudang + pengajuan.
  const whProcess = job.warehouse_process_state || "queued";
  const hasPendingReq = job.pending_request && job.pending_request.status === "pending";
  const isSent = job.warehouse_state === "sent";
  const isProcessing = isSent && whProcess === "processing";
  const isQueued = isSent && !isProcessing;
  const isOnHold = job.warehouse_state === "on_hold";
  const isCompleted = job.warehouse_state === "completed"; // Iter 394
  const isFixDesign = job.status === "FIX";
  let whLabel = null;
  let whMode = "";
  if (isCancelPending(job)) {
    whLabel = "Menunggu Keputusan Pembatalan"; whMode = "pending";
  } else if (job.status === "BATAL") {
    whLabel = null;  // kartu BATAL tidak butuh info gudang di header
  } else if (hasPendingReq) {
    whLabel = "Menunggu persetujuan gudang"; whMode = "pending";
  } else if (isOnHold) {
    whLabel = "Ditahan untuk revisi"; whMode = "on_hold";
  } else if (isCompleted) {
    whLabel = "Selesai di Gudang"; whMode = "queued";
  } else if (isProcessing) {
    whLabel = "Diproses gudang"; whMode = "processing";
  } else if (isSent) {
    if (isFixDesign) { whLabel = "Sudah dikirim ke Gudang · menunggu diproses"; whMode = "queued"; }
    else { whLabel = "Gudang menunggu desain FIX"; whMode = "await_fix"; }
  } else if (job.warehouse_state === "recalled") {
    whLabel = "Ditarik kembali · siap dikirim ulang"; whMode = "on_hold";
  } else if (job.warehouse_state === "cancelled") {
    whLabel = "Kartu Gudang BATAL"; whMode = "";
  } else {
    whLabel = "Belum dikirim ke gudang"; whMode = "not_sent";
  }
  const hasThumb = !!thumbUrl;
  const hasMockupJob = activeMockups.length > 0;
  const dlMs = ["shipped", "completed", "to_return"].includes(job.shopee_order_status) ? null : deadlineMs(job.shopee_deadline_iso);
  const urgentDl = isActiveJob(job) && dlMs !== null && dlMs - Date.now() < 24 * 3600000;
  const priority = urgentDl || isOver24(job);
  return <div className={`card shopee-card shopee-card-clickable shopee-card-compact${priority ? " is-priority" : ""}`}
    data-priority={priority ? (urgentDl ? "deadline" : "over24") : undefined}
    style={{overflow:"hidden"}}
    onClick={onOpen}
    onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
    role="button"
    tabIndex={0}
    data-testid={`shopee-card-${job.id}`}>
    <div className="shopee-card-body">
      <div className="shopee-thumb-col">
        <div className={`shopee-thumb shopee-card-thumb${hasThumb ? "" : " shopee-thumb-empty"}`}
          onClick={(e)=>{ e.stopPropagation(); if (hasMockupJob && onOpenPreview) onOpenPreview(job); }}
          style={{cursor: hasMockupJob ? "zoom-in" : "default", position:"relative"}}
          data-testid={`shopee-card-thumb-${job.id}`}
          role={hasMockupJob ? "button" : undefined}
          tabIndex={hasMockupJob ? 0 : undefined}
          onKeyDown={(e)=>{ if (hasMockupJob && (e.key==="Enter"||e.key===" ")) { e.preventDefault(); e.stopPropagation(); onOpenPreview && onOpenPreview(job); } }}>
          {hasThumb
            ? <img src={thumbUrl} alt="thumb"
                onError={(e)=>{ e.currentTarget.style.display="none"; }}/>
            : <ImgIcon size={26} className="shopee-thumb-icon" aria-hidden="true"/>}
          {/* Iter 514 — Badge "N Mockup" pada kartu dengan >1 mockup aktif.
              Mengindikasikan ke user bahwa klik Preview akan membuka gallery
              berisi semua mockup. Tidak mengubah workflow / file. */}
          {activeMockups.length > 1 && (
            <span className="shopee-thumb-count-badge"
              data-testid={`shopee-card-mockup-count-${job.id}`}
              style={{position:"absolute", bottom:4, right:4, background:"rgba(249,115,22,.95)",
                color:"#fff", borderRadius:999, padding:"1px 7px", fontSize:10.5, fontWeight:700,
                letterSpacing:0.2, lineHeight:1.4, boxShadow:"0 1px 2px rgba(0,0,0,.3)"}}>
              {activeMockups.length} Mockup
            </span>
          )}
        </div>
        {hasMockupJob
          ? <button type="button" className="shopee-thumb-caption is-link"
              onClick={(e)=>{ e.stopPropagation(); onOpenPreview && onOpenPreview(job); }}
              data-testid={`shopee-card-preview-${job.id}`}>
              <Eye size={12} aria-hidden="true"/> Preview
            </button>
          : <span className="shopee-thumb-caption is-muted">Belum ada mockup</span>}
      </div>
      <div className="shopee-card-info">
        <div className="shopee-card-headrow">
          <div className="shopee-card-headmain">
            {job.customer_account
              ? <div className="shopee-account-primary" data-testid={`shopee-card-account-${job.id}`}>{job.customer_account}</div>
              : <div className="shopee-account-primary shopee-account-empty" data-testid={`shopee-card-account-${job.id}`}>Tanpa akun</div>}
            <div className="shopee-order-row">
              <span className="shopee-order-number" data-testid={`shopee-card-order-${job.id}`}>{job.order_number}</span>
<TokopediaAttachments orderNumber={job.order_number}/>
              <button type="button" className="shopee-copy-sn" aria-label="Salin nomor pesanan" title="Salin nomor pesanan"
                data-testid={`shopee-card-copy-${job.id}`}
                onClick={async (e) => { e.stopPropagation();
                  try { await navigator.clipboard.writeText(job.order_number); toast.success("Nomor pesanan disalin"); }
                  catch { toast.error("Gagal menyalin nomor pesanan"); } }}><Copy size={13}/></button>
            </div>
            {(job.is_test || job.shopee_linked === false) && <div className="shp-card-tags">
              {job.is_test && <span className="shp-tag is-test" data-testid={`shopee-card-test-${job.id}`}>UJI</span>}
              {job.shopee_linked === false && !job.is_test && <span className="shp-tag is-manual" data-testid={`shopee-card-manual-${job.id}`}
                title="Nomor ini tidak ditemukan di data sinkronisasi Shopee">Manual — tidak terhubung Shopee</span>}
            </div>}
            <StaleBadge job={job}/>
          </div>
          <div className="shopee-card-headside">
            <span className="shopee-status-badge" style={{background:s.bg, color:s.c}}
              data-testid={`shopee-card-status-${job.id}`}>
              <s.Icon size={12} strokeWidth={2.5} aria-hidden="true"/>
              <span className="shopee-status-text">{s.t}</span>
            </span>
            <ShopeeDeadlineChip job={job}/>
            <TokopediaDeadline orderNumber={job.order_number}/>
            <CardPaperToggle job={job} onChanged={onPatched}/>
            {whLabel && <div className="shopee-wh-inline" data-mode={whMode}
              data-testid={`shopee-card-wh-${job.id}`}>
              <WarehouseIcon size={12} aria-hidden="true"/> {whLabel}
            </div>}
            {/* Iter 541 — Kebab menu HOLD/Pembatalan di header kartu aktif.
                Scope: Shopee only. Backend auto-route pengajuan ke shopee_jobs
                (karena endpoint-nya cari di orders dulu → shopee_jobs fallback). */}
            <HoldKebabMenu
              orderNumber={job.order_number}
              customerName={job.customer_account || ""}
              held={Boolean((job.hold || {}).active)}
              canceled={job.cancellation_status === "canceled_internal"}
              menuId={`card:${job.id}`}
              onDone={() => axios.get(`${API}/shopee/${job.id}`).then(r => onPatched?.(r.data)).catch(() => {})}
              testIdPrefix={`shopee-card-hold-${job.id}`}
            />
          </div>
        </div>
        {job.design_title && <div className="shopee-title-line">{job.design_title}</div>}
        {job.is_repeat_order && <div style={{margin:"4px 0 2px"}}>
          <RepeatOrderBadge active repeatRef={job.repeat_ref} context="online" size="compact"
            testid={`shopee-card-repeat-${job.id}`}/>
        </div>}
        <button type="button" className="shp-more-toggle" aria-expanded={moreOpen}
          onClick={e => { e.stopPropagation(); setMoreOpen(v => !v); }} data-testid={`shopee-card-more-${job.id}`}>
          {moreOpen ? "Tutup ▴" : "Lainnya ▾"}</button>
        <div onClick={e => e.stopPropagation()} style={{margin:"4px 0"}} data-testid={`shopee-card-form-${job.id}`}>
          <DesignFormButton channel={_dfmChannel()} orderNumber={job.order_number} jobId={job.id}/>
        </div>
        {moreOpen && <div className="shp-card-more" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}
          data-testid={`shopee-card-extras-${job.id}`}>
          <MockupTemplateButton channel={_dfmChannel()} orderNumber={job.order_number} jobId={job.id}
            defaultPaper={jobPaper(job).startsWith("A4") ? "A4P" : "A5L"}/>
        </div>}
        <div className={`shopee-card-meta shp-card-extras${moreOpen ? " is-open" : ""}`}>
          <span>Desainer: <b>{job.pic_designer_name}</b></span>
          <span>·</span>
          <span>{job.job_type === "lembur" ? "Lembur" : "Reguler"}</span>
          {job.process && <>
            <span>·</span>
            <span data-testid={`shopee-card-process-${job.id}`}>{SHOPEE_PROCESS_LABEL[job.process] || job.process}</span>
          </>}
          <span>·</span>
          <span>{fmtDate(job.created_at)}</span>
          <span>·</span>
          {hasMockupJob
            ? <span>{activeMockups.length} mockup</span>
            : <span className="shopee-mockup-empty">Belum ada mockup</span>}
        </div>
        {hasMockupJob && aiReads && <CardAiStatus job={job} reads={aiReads}/>}
      </div>
    </div>
    {/* Iter 287w — Aksi cepat kartu (max 2 tombol terlihat + ⋯) */}
    <ShopeeQuickActions job={job} activeMockups={activeMockups} isSupervisor={isSupervisor}
      onSendWarehouse={onSendWarehouse} busyKind={busyId}
      onAction={(a, p) => onQuickAction && onQuickAction(a, job, p)}/>
  </div>;
}


// Iter 287w — Bar aksi cepat kartu desainer Shopee.
// Iter 287ah — Prioritas: status desain × state Gudang × pengajuan.
// Iter 485 — Menu ••• (extras) dirapikan: hanya 3 aksi kontekstual.
//   • Ubah Desain          → `detail` (bila primary bukan detail & status ≠ BATAL)
//   • Tandai Revisi        → `revise` (bila status ∈ {FIX, BELUM_FIX} & tidak terkunci & tidak pending)
//   • Kembalikan ke Belum Fix → `unfix` (bila status ∈ {FIX, REVISI} & tidak terkunci & tidak pending)
//   Preview / Tambah Mockup / Tempel / Cetak / Riwayat dihilangkan dari menu.
//   Logika fitur tetap tersedia di tempat lain (modal detail, tombol primary, dll).
// "Ubah Desain" membuka detail (edit flow) tanpa mengubah status; perubahan
// status hanya terjadi via aksi eksplisit di dalam modal detail.
export function ShopeeQuickActions({ job, activeMockups, onSendWarehouse, onAction, busyKind, isSupervisor }) {
  const stop = (e) => e.stopPropagation();
  const isBatal = job.status === "BATAL";
  const isFix = job.status === "FIX";
  const isSent = job.warehouse_state === "sent";
  const isCompleted = job.warehouse_state === "completed"; // Iter 394 — Gudang selesai
  const isForwarded = isSent || isCompleted;
  const isProcessing = isSent && (job.warehouse_process_state || "queued") === "processing";
  const isQueued = isSent && !isProcessing;
  const isOnHold = job.warehouse_state === "on_hold";
  const hasPending = job.pending_request && job.pending_request.status === "pending";
  const hasMockup = activeMockups.length > 0;

  let primary = null;
  let danger = null;
  let note = "";
  let blockedSend = null;

  if (isBatal) {
    primary = { key: "detail", label: "Lihat Detail", icon: <Eye size={14}/>, tone: "neutral", full: true };
  } else if (hasPending) {
    primary = { key: "detail", label: "Lihat Pengajuan", icon: <AlertCircle size={14}/>, tone: "neutral", full: true };
    note = "Pengajuan sedang menunggu keputusan Gudang.";
  } else if (isProcessing) {
    primary = { key: "request_hold", label: "Ajukan Revisi", icon: <PenSquare size={14}/>, tone: "revise" };
    danger  = { key: "request_cancel", label: "Ajukan Batal", icon: <X size={14}/>, tone: "danger" };
    note = "Perubahan perlu persetujuan gudang.";
  } else if (!isFix) {
    // BELUM_FIX / REVISI — desainer masih boleh edit. Termasuk pekerjaan yang
    // sudah dikirim ke Gudang tapi belum diproses (sent+queued) → tetap Tandai FIX.
    if (hasMockup) {
      primary = { key: "fix", label: "Tandai FIX", icon: <CheckCircle2 size={14}/>, tone: "fix" };
    } else {
      primary = { key: "add", label: "Tambah Mockup", icon: <Plus size={14}/>, tone: "neutral" };
    }
    danger = { key: "cancel", label: "Batal", icon: <X size={14}/>, tone: "danger" };
  } else {
    // status = FIX
    const sendLabel = isOnHold ? "Kirim Revisi ke Gudang" : "Kirim ke Gudang";
    const block = (isOnHold || !isForwarded) ? sendBlock(job, hasMockup) : null;
    if (block) {
      // Tombol kirim tetap tampil (nonaktif) + alasan + tindakan yang diperlukan.
      blockedSend = sendLabel;
      note = block.reason;
      if (block.fix === "add") primary = { key: "add", label: "Tambah Mockup", icon: <Plus size={14}/>, tone: "neutral" };
      if (block.fix === "edit") primary = { key: "edit", label: "Ubah Pekerjaan", icon: <PenSquare size={14}/>, tone: "neutral" };
    } else if (!hasMockup) {
      // FIX tapi mockup terhapus semua → butuh Tambah Mockup dulu.
      primary = { key: "add", label: "Tambah Mockup", icon: <Plus size={14}/>, tone: "neutral" };
    } else if (isOnHold) {
      primary = { key: "send", label: "Kirim Revisi ke Gudang", icon: <Send size={14}/>, tone: "send" };
    } else if (!isForwarded) {
      primary = { key: "send", label: "Kirim ke Gudang", icon: <Send size={14}/>, tone: "send" };
    } else if (isCompleted) {
      // Iter 394 — Pekerjaan sudah selesai di Gudang → tidak ada aksi mengirim.
      primary = { key: "detail", label: "Lihat Detail", icon: <Eye size={14}/>, tone: "neutral" };
    } else if (isQueued) {
      // Sudah dikirim, belum diproses → boleh Ubah Desain (buka edit flow).
      primary = { key: "detail", label: "Ubah Desain", icon: <PenSquare size={14}/>, tone: "revise" };
    } else {
      // Fallback aman → buka detail.
      primary = { key: "detail", label: "Lihat Detail", icon: <Eye size={14}/>, tone: "neutral" };
    }
    danger = { key: "cancel", label: "Batal", icon: <X size={14}/>, tone: "danger" };
  }

  // Iter 485/495 — Build extras minimal (max 4). Hanya status aksi yang backend
  //   terima pada kondisi saat ini. Jika extras kosong → tombol ••• otomatis
  //   tersembunyi (lihat kondisi {extra.length > 0 && <ShopeeQuickMenu/>}).
  //   Urutan menu:
  //     1. Edit Pekerjaan           → `edit`     (metadata; TIDAK bisa saat BATAL)
  //     2. Ubah Desain              → `detail`   (file mockup; sama seperti sebelumnya)
  //     3. Tandai Revisi            → `revise`
  //     4. Kembalikan ke Belum Fix  → `unfix`
  const extra = [];
  if (!isBatal) {
    // Edit Pekerjaan — selalu tersedia selama belum BATAL. Backend akan tolak
    //   bila terkunci (processing/pending); UX notify.error yang muncul.
    extra.push({ key: "edit", label: "Ubah Pekerjaan", icon: <PenSquare size={12}/> });
    if (primary && primary.key !== "detail") {
      extra.push({ key: "detail", label: "Ubah Desain", icon: <PenSquare size={12}/> });
    }
    const canChangeStatus = !isProcessing && !hasPending;
    if (canChangeStatus && job.status !== "REVISI") {
      extra.push({ key: "revise", label: "Tandai Revisi", icon: <PenSquare size={12}/> });
    }
    if (canChangeStatus && job.status !== "BELUM_FIX") {
      extra.push({ key: "unfix", label: "Kembalikan ke Belum Fix", icon: <Clock size={12}/> });
    }
  }

  if (isSupervisor) extra.push({ key: "test_flag", label: job.is_test ? "Cabut Tanda UJI" : "Tandai sebagai UJI", icon: <Info size={12}/> });
  // Batal / Ajukan Batal dipindah ke menu ••• (aksi & konfirmasi sama).
  if (danger) { extra.push({ key: danger.key, label: danger.label, icon: <X size={12}/>, danger: true }); danger = null; }

  const runAction = (key) => {
    if (key === "send") { onSendWarehouse && onSendWarehouse(job); return; }
    onAction && onAction(key);
  };

  const btnClass = (tone) => `shopee-act-btn shopee-act-${tone || "neutral"}`;
  const isBusy = (k) => busyKind === k;

  return <div className="shopee-actions-wrap" onClick={stop} data-testid={`shopee-quick-${job.id}`}>
    <div className="shopee-actions-row">
      {primary && <button type="button"
        className={btnClass(primary.tone) + (primary.full ? " is-full" : "")}
        disabled={isBusy(primary.key) || primary.disabled}
        onClick={()=>runAction(primary.key)}
        data-testid={`shopee-quick-${primary.key}-${job.id}`}>
        {isBusy(primary.key) ? <Loader2 size={14} className="spin"/> : primary.icon}
        <span>{isBusy(primary.key)
          ? (primary.key === "send" ? "Mengirim…" : "Memproses…")
          : primary.label}</span>
      </button>}
      {blockedSend && <button type="button" className={btnClass("send") + " is-blocked"} disabled aria-disabled="true"
        title={note} data-testid={`shopee-quick-send-blocked-${job.id}`}>
        <Lock size={14}/><span>{blockedSend}</span>
      </button>}
      {danger && <button type="button"
        className={btnClass("danger") + " is-secondary"}
        disabled={isBusy(danger.key)}
        onClick={()=>runAction(danger.key)}
        data-testid={`shopee-quick-${danger.key}-${job.id}`}>
        {isBusy(danger.key) ? <Loader2 size={14} className="spin"/> : danger.icon}
        <span>{isBusy(danger.key) ? "Memproses…" : danger.label}</span>
      </button>}
      {extra.length > 0 && <ShopeeQuickMenu items={extra} onSelect={(k)=>runAction(k)} jobId={job.id}/>}
    </div>
    {note && <div className={`shopee-action-note${blockedSend ? " is-blocked" : ""}`} data-testid={`shopee-action-note-${job.id}`}>
      <Info size={11} aria-hidden="true"/> {note}
    </div>}
  </div>;
}


// Iter 287x — Menu ⋯ di-render via portal ke document.body agar tidak dipotong
// oleh overflow:hidden kartu. Posisi dihitung dari rect trigger; flip ke atas
// bila ruang bawah tidak cukup; clamp horizontal supaya tidak keluar layar.
function ShopeeQuickMenu({ items, onSelect, jobId }) {
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, placement: "bottom" });

  const computePos = () => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const menuW = 200;  // sesuai min-width CSS + sedikit padding
    const menuHEst = Math.min(items.length * 38 + 12, 320);
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = 6;
    const spaceBelow = vh - rect.bottom;
    const spaceAbove = rect.top;
    const flipUp = spaceBelow < menuHEst + gap && spaceAbove > spaceBelow;
    // Prefer align kanan-menu ke kanan-tombol; clamp horizontal ke viewport.
    let left = rect.right - menuW;
    if (left < 8) left = 8;
    if (left + menuW > vw - 8) left = vw - menuW - 8;
    const top = flipUp ? Math.max(8, rect.top - gap - menuHEst) : rect.bottom + gap;
    setPos({ top, left, placement: flipUp ? "top" : "bottom" });
  };

  useEffect(() => {
    if (!open) return;
    computePos();
    const onDoc = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onResize = () => computePos();
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc, { passive: true });
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return <>
    <button ref={btnRef} type="button" className="soft-btn small-btn shopee-quick-menu-btn"
      onClick={(e)=>{ e.stopPropagation(); setOpen(o=>!o); }}
      aria-haspopup="menu" aria-expanded={open}
      data-testid={`shopee-quick-menu-${jobId}`}>
      <MoreHorizontal size={14}/>
    </button>
    {open && createPortal(
      <div ref={menuRef} className="shopee-quick-menu shopee-theme" role="menu"
        data-placement={pos.placement}
        style={{ position:"fixed", top: pos.top, left: pos.left,
                    width: 200, zIndex: 2000 }}
        onClick={(e)=>e.stopPropagation()}
        onMouseDown={(e)=>e.stopPropagation()}
        data-testid={`shopee-quick-menu-portal-${jobId}`}>
        {items.map(it => <button key={it.key} type="button" role="menuitem"
          className={`shopee-quick-menu-item${it.danger ? " danger" : ""}`}
          onClick={()=>{ setOpen(false); onSelect(it.key); }}
          data-testid={`shopee-quick-menu-${it.key}-${jobId}`}>
          {it.icon} {it.label}
        </button>)}
      </div>,
      document.body
    )}
  </>;
}



function JobForm({ me, designers, onClose, onSaved, onDuplicateFound }) {
  const tkp = useTkpChannel() === "tokopedia";
  const chName = tkp ? "Tokopedia" : "Shopee";
  const [dlDate, setDlDate] = useState("");
  const [dlTime, setDlTime] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [customer, setCustomer] = useState("");
  const [jobType, setJobType] = useState("reguler");
  const [designTitle, setDesignTitle] = useState("");
  const [processVal, setProcessVal] = useState("");
  const [desc, setDesc] = useState("");
  // Iter 392 — PIC Designer auto-assignment:
  //   - Operator/Designer yang login: picId = me.id otomatis, UI readonly (anti-salah pilih).
  //   - Supervisor/Owner/Admin ("level=supervisor"): dropdown manual sesuai permission existing.
  //   Simpan via `operator_id` (bukan hanya nama) — backend tetap resolve designer by id.
  const isSupervisor = me?.level === "supervisor";
  const [picId, setPicId] = useState(isSupervisor ? "" : (me?.id || ""));
  useEffect(() => {
    // Sync bila `me` prop baru ter-load setelah mount (kondisi race).
    if (!isSupervisor && me?.id && picId !== me.id) setPicId(me.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.id, isSupervisor]);
  const picDisplayName = (() => {
    if (!isSupervisor) {
      // Prefer nama dari me; fallback cari di daftar designers berdasar id.
      if (me?.name) return me.name;
      const match = (designers || []).find(d => d.id === me?.id);
      return match?.name || "";
    }
    return "";
  })();
  const [isRepeat, setIsRepeat] = useState(false);
  const [repeatRef, setRepeatRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [dupJob, setDupJob] = useState(null);
  const [dupChecking, setDupChecking] = useState(false);
  const [unlinked, setUnlinked] = useState(false);

  const checkDup = async (v) => {
    setDupJob(null); setUnlinked(false);
    const val = (v || "").trim();
    if (!val) return;
    if (!tkp) axios.get(`${API}/shopee/link-check`, { params: { order_number: val } })
      .then(r => setUnlinked(r.data?.linked === false)).catch(() => {});
    setDupChecking(true);
    try {
      const r = await axios.get(`${API}/shopee/duplicate`, { params: { order_number: val } });
      if (r.data.exists) setDupJob(r.data.job);
    } catch (e) { /* ignore */ }
    finally { setDupChecking(false); }
  };

  const submittingRef = useRef(false);
  const submit = async () => {
    if (submittingRef.current) return;
    if (!orderNumber.trim()) { setErr("Nomor pesanan wajib"); return; }
    if (isSupervisor && !picId) { setErr("Pilih desainer"); return; }
    if (tkp && !dlDate) { setErr("Batas kirim Tokopedia wajib diisi"); return; }
    submittingRef.current = true;
    setBusy(true); setErr("");
    try {
      // Iter 392 — Selalu kirim operator_id yang login untuk designer (bukan hanya nama).
      //   Supervisor kirim pilihan dropdown. Backend tetap valid-check.
      const picPayload = isSupervisor ? picId : (picId || me?.id || undefined);
      const r = await axios.post(`${API}/shopee`, {
        order_number: orderNumber.trim(),
        customer_account: customer.trim(),
        job_type: jobType,
        design_title: designTitle.trim(),
        description: desc.trim(),
        process: processVal || "",
        pic_designer_id: picPayload || undefined,
        is_repeat_order: !!isRepeat,
        repeat_ref: isRepeat ? repeatRef.trim() : "",
        ...(tkp ? { deadline_date: dlDate, deadline_time: dlTime } : {}),
      });
      onSaved(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.job) {
        setDupJob(d.job);
        setErr("Nomor pesanan sudah tercatat aktif — buka pekerjaan yang ada?");
      } else {
        setErr(typeof d === "string" ? d : (d?.detail || "Gagal simpan"));
      }
    } finally { submittingRef.current = false; setBusy(false); }
  };

  return <ModalShell title={`Buat Pekerjaan ${chName}`} onClose={onClose} testid="shopee-form-modal">
    <div style={{display:"flex", flexDirection:"column", gap:8}}>
      <label style={{fontSize:12, fontWeight:600}}>Nomor Pesanan {chName} <span style={{color:"#b91c1c"}}>*</span></label>
      <input value={orderNumber} onChange={e=>{ setOrderNumber(e.target.value); setDupJob(null); }}
        onBlur={e=>checkDup(e.target.value)}
        placeholder={tkp ? "Contoh: INV/20260101/MPL/1234567890" : "Contoh: 250926ABCXYZ"}
        style={inputStyle} data-testid="shopee-form-order-number"/>
      {tkp && <>
        <label style={{fontSize:12, fontWeight:600}}>Batas Kirim <span style={{color:"#b91c1c"}}>*</span> <span className="muted small" style={{fontWeight:500}}>(nomor baru otomatis didaftarkan ke Pesanan Tokopedia · jam kosong = 23:59 WIB)</span></label>
        <div className="shp-form-2col" style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:8}}>
          <input type="date" value={dlDate} onChange={e=>setDlDate(e.target.value)} style={inputStyle} aria-label="Tanggal batas kirim" data-testid="tkp-form-deadline-date"/>
          <input type="time" value={dlTime} onChange={e=>setDlTime(e.target.value)} style={inputStyle} aria-label="Jam batas kirim (WIB)" data-testid="tkp-form-deadline-time"/>
        </div>
      </>}
      {dupChecking && <div className="muted small"><Loader2 size={12} className="spin"/> Cek duplikat…</div>}
      {unlinked && <div className="warn-box small" data-testid="shopee-form-unlinked-warning">
        Nomor ini tidak ditemukan di data sinkronisasi Shopee. Pekerjaan tetap bisa dibuat dan akan berlabel
        "Manual — tidak terhubung Shopee". Periksa kembali nomornya agar scan & sinkronisasi tidak keliru.</div>}
      {dupJob && <div style={{padding:"8px 10px", background:"#fef3c7", border:"1px solid #f59e0b", borderRadius:6, fontSize:12}} data-testid="shopee-form-dup-warning">
        Nomor sudah ada. PIC: <b>{dupJob.pic_designer_name}</b> · Status: <b>{STATUS_LABELS[dupJob.status]?.t}</b>
        {" "}<button className="link-btn" onClick={()=>onDuplicateFound(dupJob)} data-testid="shopee-form-dup-open">Buka pekerjaan</button>
      </div>}
      <label style={{fontSize:12, fontWeight:600}}>Akun Pelanggan</label>
      <input value={customer} onChange={e=>setCustomer(e.target.value)} placeholder="Nama toko / akun"
        style={inputStyle} data-testid="shopee-form-customer"/>
      <div className="shp-form-2col" style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:8}}>
        <div>
          <label style={{fontSize:12, fontWeight:600}}>Jenis</label>
          <select value={jobType} onChange={e=>setJobType(e.target.value)} style={inputStyle}
            data-testid="shopee-form-jobtype">
            <option value="reguler">Reguler</option>
            <option value="lembur">Lembur</option>
          </select>
        </div>
        <div>
          <label style={{fontSize:12, fontWeight:600}}>Desainer</label>
          {isSupervisor
            ? <select value={picId} onChange={e=>setPicId(e.target.value)} style={inputStyle}
                data-testid="shopee-form-pic">
                <option value="">— pilih —</option>
                {designers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            : <input readOnly value={picDisplayName}
                style={{...inputStyle, background:"#f8fafc", cursor:"not-allowed"}}
                data-testid="shopee-form-pic-readonly"
                title="Desainer otomatis diisi dari akun yang login"
                aria-label="Desainer (otomatis)"/>}
        </div>
      </div>
      <label style={{fontSize:12, fontWeight:600}}>Judul Desain</label>
      <input value={designTitle} onChange={e=>setDesignTitle(e.target.value)}
        placeholder="Contoh: Kaos Combed Depan"
        style={inputStyle} data-testid="shopee-form-title"/>
      <label style={{fontSize:12, fontWeight:600}}>Proses Pengerjaan <span className="muted small" style={{fontWeight:500}}>(wajib sebelum kirim ke Gudang)</span></label>
      <select value={processVal} onChange={e=>setProcessVal(e.target.value)} style={inputStyle}
        data-testid="shopee-form-process">
        <option value="">— pilih —</option>
        {SHOPEE_PROCESS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <label style={{fontSize:12, fontWeight:600}}>Keterangan</label>
      <textarea value={desc} onChange={e=>setDesc(e.target.value)} rows={3}
        style={{...inputStyle, resize:"vertical"}} data-testid="shopee-form-desc"/>
      <RepeatOrderFields
        active={isRepeat}
        repeatRef={repeatRef}
        onChange={({ is_repeat_order, repeat_ref }) => { setIsRepeat(is_repeat_order); setRepeatRef(repeat_ref); }}
        context="online"
        idPrefix="shopee-form-repeat"/>
      {err && <div className="warn-box small" data-testid="shopee-form-error">{err}</div>}
      <div className="shp-modal-actions" style={{display:"flex", gap:8, justifyContent:"flex-end", marginTop:6}}>
        <button className="soft-btn small-btn" onClick={onClose} disabled={busy} data-testid="shopee-form-cancel">Batal</button>
        <button className="primary small-btn" onClick={submit} disabled={busy || dupJob} data-testid="shopee-form-submit">
          {busy ? "Menyimpan…" : "Simpan"}
        </button>
      </div>
    </div>
  </ModalShell>;
}


// Iter 495 — EditJobForm: edit metadata pekerjaan existing (bukan buat baru).
//   Field aman yang boleh diedit: akun customer, judul desain, proses, jenis
//   (reguler/lembur), keterangan, repeat order + referensi, PIC designer (hanya
//   supervisor). Nomor Pesanan Shopee DIKUNCI (read-only). Transaksi/order
//   asli, qty, pembayaran, state Gudang, histori, state produksi TIDAK diubah.
//   Backend PATCH /shopee/{id} sudah menolak saat terkunci (processing/BATAL)
//   — error ditampilkan di UI tanpa membuat pekerjaan baru.
// Dipakai halaman HOLD/Pembatalan: Ubah Pekerjaan / Ganti Mockup memakai formulir & panel mockup yang sama.
// HOLD tetap aktif; unggah mockup tidak membuat FIX dan tidak membuka HOLD (aturan server).
export function HoldJobEditHost({ jobId, mode, onClose }) {
  const [job, setJob] = useState(null);
  const [err, setErr] = useState("");
  const load = async () => {
    try { setJob((await axios.get(`${API}/shopee/${jobId}`)).data); }
    catch (e) { setErr(formatApiError(e, "Gagal memuat pekerjaan.")); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [jobId]);
  if (!job) return <ModalShell title="Memuat pekerjaan…" onClose={() => onClose(false)} testid="hold-edit-loading">
    {err ? <div className="warn-box small" data-testid="hold-edit-error">{err}</div> : <div className="muted small"><Loader2 size={12} className="spin"/> Memuat…</div>}
  </ModalShell>;
  if (mode === "edit") return <EditJobForm job={job} me={{}} designers={[]} onClose={() => onClose(false)} onSaved={() => onClose(true)}/>;
  return <ModalShell title={`Ganti Desain — ${job.order_number}`} onClose={() => onClose(true)} testid="hold-mockup-modal" wide>
    <div className="warn-box small" style={{marginBottom:8}} data-testid="hold-mockup-note">
      Pesanan tetap Ditahan. Mengganti desain tidak membuat FIX dan tidak melanjutkan pekerjaan.
      Bila sebelumnya FIX, status menjadi REVISI — Tandai FIX lagi setelah pekerjaan dilanjutkan, sebelum Kirim ke Gudang.</div>
    <MockupsSection job={job} canEdit={job.status !== "BATAL"} onChanged={load}/>
  </ModalShell>;
}

function EditJobForm({ job, me, designers, onClose, onSaved }) {
  const isSupervisor = me?.level === "supervisor";
  const [customer, setCustomer] = useState(job?.customer_account || "");
  const [jobType, setJobType] = useState(job?.job_type || "reguler");
  const [designTitle, setDesignTitle] = useState(job?.design_title || "");
  const [processVal, setProcessVal] = useState(job?.process || "");
  const [desc, setDesc] = useState(job?.description || "");
  const [picId, setPicId] = useState(job?.pic_designer_id || "");
  const [isRepeat, setIsRepeat] = useState(!!job?.is_repeat_order);
  const [repeatRef, setRepeatRef] = useState(job?.repeat_ref || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Iter 495 — Peringatan: pekerjaan sudah diteruskan ke Gudang.
  //   Backend tetap izinkan edit field metadata selama belum processing/BATAL,
  //   dan akan auto-reset status desain ke REVISI bila masih queued di Gudang.
  const isSent = job?.warehouse_state === "sent";
  const isProcessing = isSent && (job?.warehouse_process_state || "queued") === "processing";
  const isCompleted = job?.warehouse_state === "completed";
  const isPendingReq = job?.pending_request && job.pending_request.status === "pending";
  const locked = isProcessing || isCompleted || isPendingReq || job?.status === "BATAL";
  const lockReason = job?.status === "BATAL" ? "Pekerjaan BATAL — tidak dapat diubah."
    : isCompleted ? "Pekerjaan sudah selesai di Gudang — tidak dapat diubah."
    : isProcessing ? "Pekerjaan sedang diproses Gudang — ajukan Revisi/Batal."
    : isPendingReq ? "Pengajuan masih menunggu keputusan Gudang."
    : "";

  const submit = async () => {
    if (!job?.id) return;
    setBusy(true); setErr("");
    try {
      // 1) PATCH metadata
      const payload = {
        customer_account: customer.trim(),
        job_type: jobType,
        design_title: designTitle.trim(),
        description: desc.trim(),
        process: processVal || "",
        is_repeat_order: !!isRepeat,
        repeat_ref: isRepeat ? repeatRef.trim() : "",
      };
      const r = await axios.patch(`${API}/shopee/${job.id}`, payload);
      let updated = r.data;
      // 2) Transfer PIC (opsional, supervisor only, bila berubah)
      if (isSupervisor && picId && picId !== (job?.pic_designer_id || "")) {
        try {
          const t = await axios.post(`${API}/shopee/${job.id}/transfer-pic`, { pic_designer_id: picId });
          updated = t.data;
        } catch (te) {
          const detail = te?.response?.data?.detail;
          notify.warning({
            title: "Metadata tersimpan, desainer gagal diubah",
            message: typeof detail === "string" ? detail : "Pindah desainer ditolak sistem.",
          });
        }
      }
      notify.success({ title: "Perubahan tersimpan", message: `${job.order_number} diperbarui.` });
      onSaved && onSaved(updated);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : (d?.detail || "Gagal menyimpan perubahan."));
    } finally { setBusy(false); }
  };

  return <ModalShell title="Ubah Pekerjaan Shopee" onClose={onClose} testid="shopee-edit-modal">
    <div style={{display:"flex", flexDirection:"column", gap:8}}>
      {lockReason && (
        <div className="warn-box small" data-testid="shopee-edit-lock-note">
          {lockReason}
        </div>
      )}

      <label style={{fontSize:12, fontWeight:600}}>Nomor Pesanan Shopee <span className="muted small" style={{fontWeight:500}}>(terkunci)</span></label>
      <input readOnly value={job?.order_number || ""}
        style={{...inputStyle, background:"#f8fafc", cursor:"not-allowed"}}
        data-testid="shopee-edit-order-number"
        title="Nomor Pesanan Shopee tidak dapat diubah dari menu ini"
        aria-label="Nomor Pesanan (readonly)"/>

      <label style={{fontSize:12, fontWeight:600}}>Akun Pelanggan</label>
      <input value={customer} onChange={e=>setCustomer(e.target.value)}
        placeholder="Nama toko / akun" disabled={locked}
        style={inputStyle} data-testid="shopee-edit-customer"/>

      <div className="shp-form-2col" style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:8}}>
        <div>
          <label style={{fontSize:12, fontWeight:600}}>Jenis</label>
          <select value={jobType} onChange={e=>setJobType(e.target.value)} disabled={locked}
            style={inputStyle} data-testid="shopee-edit-jobtype">
            <option value="reguler">Reguler</option>
            <option value="lembur">Lembur</option>
          </select>
        </div>
        <div>
          <label style={{fontSize:12, fontWeight:600}}>
            PIC Designer {!isSupervisor && <span className="muted small" style={{fontWeight:500}}>(hanya supervisor)</span>}
          </label>
          {isSupervisor
            ? <select value={picId} onChange={e=>setPicId(e.target.value)} disabled={locked}
                style={inputStyle} data-testid="shopee-edit-pic">
                <option value="">— pilih —</option>
                {(designers || []).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            : <input readOnly value={job?.pic_designer_name || ""}
                style={{...inputStyle, background:"#f8fafc", cursor:"not-allowed"}}
                data-testid="shopee-edit-pic-readonly"/>}
        </div>
      </div>

      <label style={{fontSize:12, fontWeight:600}}>Judul Desain</label>
      <input value={designTitle} onChange={e=>setDesignTitle(e.target.value)} disabled={locked}
        placeholder="Contoh: Kaos Combed Depan"
        style={inputStyle} data-testid="shopee-edit-title"/>

      <label style={{fontSize:12, fontWeight:600}}>Proses Pengerjaan</label>
      <select value={processVal} onChange={e=>setProcessVal(e.target.value)} disabled={locked}
        style={inputStyle} data-testid="shopee-edit-process">
        <option value="">— pilih —</option>
        {SHOPEE_PROCESS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>

      <label style={{fontSize:12, fontWeight:600}}>Keterangan / Catatan</label>
      <textarea value={desc} onChange={e=>setDesc(e.target.value)} rows={3} disabled={locked}
        style={{...inputStyle, resize:"vertical"}} data-testid="shopee-edit-desc"/>

      <RepeatOrderFields
        active={isRepeat}
        repeatRef={repeatRef}
        onChange={({ is_repeat_order, repeat_ref }) => { setIsRepeat(is_repeat_order); setRepeatRef(repeat_ref); }}
        context="online"
        idPrefix="shopee-edit-repeat"
        disabled={locked}/>

      {err && <div className="warn-box small" data-testid="shopee-edit-error">{err}</div>}

      <div className="shp-modal-actions" style={{display:"flex", gap:8, justifyContent:"flex-end", marginTop:6}}>
        <button className="soft-btn small-btn" onClick={onClose} disabled={busy}
          data-testid="shopee-edit-cancel">Batal</button>
        <button className="primary small-btn" onClick={submit} disabled={busy || locked}
          data-testid="shopee-edit-submit">
          {busy ? "Menyimpan…" : "Simpan Perubahan"}
        </button>
      </div>
    </div>
  </ModalShell>;
}


// Iter 287ae — Inline editor "Proses Pengerjaan" pada detail pekerjaan.
// Nonaktif jika terkunci/BATAL; PATCH ke /api/shopee/{id} sesuai izin backend.
function ShopeeProcessInlineEditor({ job, canEdit, onSaved }) {
  const [val, setVal] = useState(job?.process || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(job?.process || ""); }, [job?.process]);
  if (!canEdit) {
    return <div style={{fontWeight:600}} data-testid="shopee-detail-process-readonly">
      {job?.process ? SHOPEE_PROCESS_LABEL[job.process] : "-"}
    </div>;
  }
  const save = async (newVal) => {
    if (newVal === (job?.process || "")) return;
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/shopee/${job.id}`, { process: newVal });
      onSaved && onSaved(r.data);
      notify.success({ title: "Tersimpan", message: "Proses Pengerjaan diperbarui." });
    } catch (e) {
      notify.error({ title: "Gagal simpan proses", message: formatApiError(e, "Gagal menyimpan.") });
      setVal(job?.process || "");
    } finally { setBusy(false); }
  };
  return <select value={val}
    onChange={e => { setVal(e.target.value); save(e.target.value); }}
    disabled={busy}
    className="shopee-process-inline-select"
    data-testid="shopee-detail-process-select">
    <option value="">— pilih —</option>
    {SHOPEE_PROCESS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>;
}


// Iter 391 — Inline editor Repeat Order di detail pekerjaan Shopee.
//   Mengirim PATCH {is_repeat_order, repeat_ref} tanpa menyentuh field lain.
function RepeatOrderInlineEditor({ job, canEdit, onSaved }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(!!job?.is_repeat_order);
  const [ref, setRef] = useState(job?.repeat_ref || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setActive(!!job?.is_repeat_order); setRef(job?.repeat_ref || ""); }, [job?.is_repeat_order, job?.repeat_ref]);
  if (!canEdit) return null;
  const save = async (payload) => {
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/shopee/${job.id}`, payload);
      onSaved && onSaved(r.data);
      notify.success({ title: "Tersimpan", message: "Penanda Repeat Order diperbarui." });
      setOpen(false);
    } catch (e) {
      notify.error({ title: "Gagal simpan", message: formatApiError(e, "Gagal menyimpan.") });
    } finally { setBusy(false); }
  };
  if (!open) {
    return <button type="button" className="link-btn" style={{fontSize:12}}
      onClick={()=>setOpen(true)} data-testid="shopee-detail-repeat-edit">
      {job?.is_repeat_order ? "Ubah penanda Repeat Order" : "+ Tandai sebagai Repeat Order"}
    </button>;
  }
  return <div style={{flexBasis:"100%", maxWidth:420}}>
    <RepeatOrderFields active={active} repeatRef={ref}
      onChange={({ is_repeat_order, repeat_ref }) => { setActive(is_repeat_order); setRef(repeat_ref); }}
      context="online" disabled={busy} idPrefix="shopee-detail-repeat"/>
    <div style={{display:"flex", gap:6, marginTop:6, justifyContent:"flex-end"}}>
      <button type="button" className="soft-btn small-btn" disabled={busy}
        onClick={()=>{ setActive(!!job?.is_repeat_order); setRef(job?.repeat_ref || ""); setOpen(false); }}
        data-testid="shopee-detail-repeat-cancel">Batal</button>
      <button type="button" className="primary small-btn" disabled={busy}
        onClick={()=>save({ is_repeat_order: !!active, repeat_ref: active ? (ref || "").trim() : "" })}
        data-testid="shopee-detail-repeat-save">
        {busy ? "Menyimpan…" : "Simpan"}
      </button>
    </div>
  </div>;
}


function JobDetail({ jobId, me, designers, onClose, onUpdated }) {
  const [job, setJobState] = useState(null);
  // Iter 287ai — Wrapper setJob yang juga menotifikasi parent supaya kartu di
  // daftar ikut update tanpa refetch (menghilangkan jeda tombol Kirim).
  const setJob = (next) => {
    setJobState(next);
    if (next && next.id && onUpdated) onUpdated(next);
  };
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(""); // "batal" | "transfer"
  const printFrameRef = useRef(null);
  const load = async () => {
    setLoading(true); setErr("");
    try {
      const r = await axios.get(`${API}/shopee/${jobId}`);
      setJob(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || "Gagal memuat"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [jobId]);

  const isSupervisor = me?.level === "supervisor";
  // Iter 287g/287v — canEdit dibatasi juga oleh warehouse lock. Backend memvalidasi ulang.
  // Editable pada: state != sent, ATAU state==sent tapi belum processing, ATAU state==on_hold.
  const isProcessingLocked = job?.warehouse_state === "sent" && (job?.warehouse_process_state || "queued") === "processing";
  const isOnHold = job?.warehouse_state === "on_hold";
  const canEdit = job && job.status !== "BATAL" && !isProcessingLocked;
  // Mockup tetap boleh direvisi walau Gudang memproses; status kembali REVISI sampai desainer FIX (Lanjutkan).
  const canEditMockup = !!job && job.status !== "BATAL";

  const setStatus = async (status, reason = "", checkReason = "") => {
    try {
      await axios.post(`${API}/shopee/${jobId}/status`, { status, reason, check_reason: checkReason });
      await load();
    } catch (e) { setErr(formatApiError(e, "Gagal ubah status")); }
  };

  const doPrint = () => {
    // Gunakan blob URL supaya window baru dijamin ter-load penuh sebelum print.
    // Sebelumnya pakai document.write() rentan blank di sebagian browser.
    const html = buildPrintHTML(job);
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank", "width=900,height=1000");
    if (!w) {
      // Iter 287p — Ganti alert dgn notify.warning.
      notify.warning({ title: "Popup diblokir", message: "Izinkan pop-up pada browser Anda untuk membuka jendela cetak." });
      return;
    }
    // Cleanup URL setelah beberapa detik agar tidak leak.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    // Delay minimal 1.5 dtk agar gambar sempat termuat sebelum print dialog.
    setTimeout(() => { try { w.focus(); w.print(); } catch {} }, 1500);
  };

  // Iter 287g — Setelah sent, pekerjaan LOCKED. Tidak ada revisi langsung dari sini;
  // Owner/Supervisor harus Tarik Kembali dulu. Tombol send hanya muncul kalau state != sent.
  const [sending, setSending] = useState(false);
  const [recallOpen, setRecallOpen] = useState(false);
  const isLocked = isProcessingLocked;
  const pendingReq = job?.pending_request && job.pending_request.status === "pending" ? job.pending_request : null;
  // Backend validates PIC/supervisor; UI menampilkan tombol kapanpun locked & tanpa pending.
  const canRequest = isLocked && !pendingReq;
  const sendToWarehouse = async () => {
    if (!job || sending) return;
    // Iter 287o — Hitung hanya mockup aktif (belum dihapus) untuk validasi kirim.
    const activeCount = (job.mockups || []).filter(m => !m.deleted_at).length;
    if (!activeCount) {
      notify.warning({ title: "Belum ada mockup aktif", message: "Minimal 1 mockup aktif wajib tersimpan sebelum dikirim ke Gudang." });
      return;
    }
    // Iter 287ae — Proses Pengerjaan WAJIB diisi sebelum kirim.
    if (!(job.process || "").trim()) {
      notify.warning({ title: "Pilih Proses Pengerjaan", message: "Set 'Proses Pengerjaan' (Bordir / Sablon DTF / Bordir+Sablon DTF / Sublim/Full Print) terlebih dahulu." });
      return;
    }
    // Iter 287p — Ganti window.confirm dgn notify.confirm (modal in-app oranye).
    const ok = await notify.confirm({
      title: "Kirim ke Gudang?",
      message: `Mockup pesanan ${job.order_number} akan dikirim ke Gudang. Setelah dikirim, pekerjaan terkunci dan tidak bisa diedit.`,
      primaryLabel: "Kirim Sekarang",
      secondaryLabel: "Batal",
    });
    if (!ok) return;
    setSending(true); setErr("");
    try {
      // Iter 287ai — pakai respons endpoint langsung untuk update state; jangan
      // refetch list/full detail (menghilangkan jeda "sudah dikirim tapi tombol masih aktif").
      const r = await axios.post(`${API}/shopee/${jobId}/send-to-warehouse`);
      if (r?.data) setJob(r.data);
      notify.success({ title: "Berhasil", message: "Mockup berhasil dikirim ke Gudang." });
    } catch (e) {
      setErr(formatApiError(e, "Gagal kirim ke Gudang"));
      notify.error({ title: "Gagal kirim ke Gudang", message: formatApiError(e, "Gagal kirim ke Gudang") });
    }
    finally { setSending(false); }
  };

  return <ModalShell title={job ? `Pekerjaan ${job.order_number}` : "Detail Pekerjaan"} onClose={onClose} testid="shopee-detail-modal" wide>
    {loading && <div className="muted small"><Loader2 size={14} className="spin"/> Memuat…</div>}
    {err && <div className="warn-box small">{err}</div>}
    {job && <div style={{display:"flex", flexDirection:"column", gap:12}}>
      <div style={{display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:8, alignItems:"center"}}>
        <div>
          <StatusBadge status={job.status}/>
          <span style={{marginLeft:8}} className="muted small">
            {job.job_type === "lembur" ? "Lembur" : "Reguler"} · Masuk: {fmtDate(job.created_at)} · PIC: <b>{job.pic_designer_name}</b>
          </span>
        </div>
        <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
          <button className="soft-btn small-btn" onClick={doPrint} data-testid="shopee-detail-print"><Printer size={14}/> Cetak</button>
          {/* Iter 287g — Semua tombol mutasi disembunyikan saat LOCKED. Preview/Cetak/Riwayat tetap ada. */}
          {canEditMockup && job.status !== "FIX" && (() => {
            const noMockup = !(job.mockups || []).some(m => !m.deleted_at);
            return <><FormCheckFix job={job} disabled={noMockup} onFix={(r)=>setStatus("FIX", "", r)}/>
              {noMockup && <div className="warn-box small" style={{flexBasis:"100%", margin:0}} data-testid="shopee-detail-fix-blocked">
                <Lock size={12}/> Tandai FIX belum bisa: tambahkan minimal 1 mockup terlebih dahulu.</div>}</>;
          })()}
          {!isLocked && canEdit && job.status === "FIX" && <button className="soft-btn small-btn" onClick={()=>setStatus("REVISI")}
            data-testid="shopee-detail-revisi">Tandai REVISI</button>}
          {/* Iter 287ai — Sembunyikan tombol Kirim setelah `warehouse_state === "sent"`
              supaya klik ulang tidak mungkin. Tombol muncul untuk not-sent atau on_hold.
              Iter 394 — Juga sembunyikan untuk `completed` (sudah ACC Gudang). */}
          {!isLocked && canEdit && job.status === "FIX"
            && (job.mockups || []).filter(m => !m.deleted_at).length > 0
            && !["sent", "completed"].includes(job.warehouse_state) && (
            <button className="primary small-btn" onClick={sendToWarehouse} disabled={sending}
              data-testid="shopee-detail-send-warehouse">
              {sending ? <Loader2 size={14} className="spin"/> : <Send size={14}/>}
              {" "}{sending ? "Mengirim…" : (isOnHold ? "Kirim Revisi ke Gudang" : "Kirim ke Gudang")}
            </button>
          )}
          {job.status === "FIX" && !["sent", "completed"].includes(job.warehouse_state) && (() => {
            const b = sendBlock(job, (job.mockups || []).some(m => !m.deleted_at));
            return b && <div className="warn-box small" style={{flexBasis:"100%", margin:0}} data-testid="shopee-detail-send-blocked">
              <Lock size={12}/> {b.reason}</div>;
          })()}
          {/* Iter 287v — Pengajuan Tahan/Batal saat LOCKED (processing) */}
          {canRequest && <button className="soft-btn small-btn"
            style={{color:"var(--tf-neutral)", borderColor:"var(--tf-neutral-border)"}}
            onClick={()=>setAction("request_hold")} data-testid="shopee-detail-request-hold">
            <AlertCircle size={14}/> Ajukan Tahan/Revisi
          </button>}
          {canRequest && <button className="soft-btn small-btn shopee-btn-danger"
            onClick={()=>setAction("request_cancel")} data-testid="shopee-detail-request-cancel">
            <Ban size={14}/> Ajukan Batal
          </button>}
          {!isLocked && canEdit && <button className="soft-btn small-btn shopee-btn-danger"
            onClick={()=>setAction("batal")} data-testid="shopee-detail-cancel"><Ban size={14}/> Batalkan</button>}
          {!isLocked && isSupervisor && canEdit && <button className="soft-btn small-btn"
            onClick={()=>setAction("transfer")} data-testid="shopee-detail-transfer"><UsersIcon size={14}/> Pindah Desainer</button>}
          {/* Iter 287g — Tarik Kembali dari Gudang: Owner/Supervisor only, wajib alasan. */}
          {isLocked && isSupervisor && <button className="soft-btn small-btn"
            style={{color:"#b91c1c", borderColor:"#fecaca"}}
            onClick={()=>setRecallOpen(true)} data-testid="shopee-detail-recall">
            Tarik Kembali dari Gudang
          </button>}
        </div>
      </div>

      <div className="card" style={{padding:14}}>
        {job.is_repeat_order && <div style={{marginBottom:10, display:"flex", alignItems:"center", gap:8, flexWrap:"wrap"}}>
          <RepeatOrderBadge active repeatRef={job.repeat_ref} context="online" testid="shopee-detail-repeat-badge"/>
          <RepeatOrderInlineEditor job={job} canEdit={canEdit && !isLocked} onSaved={(u)=>setJob(u)}/>
        </div>}
        {!job.is_repeat_order && canEdit && !isLocked && <div style={{marginBottom:10}}>
          <RepeatOrderInlineEditor job={job} canEdit onSaved={(u)=>setJob(u)}/>
        </div>}
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:12, fontSize:13}}>
          <div>
            <div className="muted small">Akun Pelanggan</div>
            <div className="shopee-account-line" data-testid="shopee-detail-account"><b>{job.customer_account || "-"}</b>
              {job.designer_history && <> <DesignerHistoryChip history={job.designer_history} testid="shopee-detail-designer-history"/></>}</div>
          </div>
          <div><div className="muted small">Judul Desain</div><div style={{fontWeight:600}}>{job.design_title || "-"}</div></div>
          <div>
            <div className="muted small">Proses Pengerjaan</div>
            <ShopeeProcessInlineEditor job={job} canEdit={canEdit && !isLocked} onSaved={(u)=>setJob(u)}/>
          </div>
          <div><div className="muted small">Durasi sampai FIX</div><div style={{fontWeight:600}}>
            {job.first_fix_at ? `${fmtDurationSince(job.created_at, job.first_fix_at)} (bukan jam kerja aktif)` : "Belum FIX"}
          </div></div>
          <div><div className="muted small">FIX pertama</div><div style={{fontWeight:600}}>
            {job.first_fix_at ? `${fmtDate(job.first_fix_at)} · ${job.first_fix_pic_name || "-"}` : "-"}
          </div></div>
        </div>
        {job.description && <div style={{marginTop:8, padding:"6px 8px", background:"#f8fafc", borderRadius:6, fontSize:12}}>
          <div className="muted small" style={{marginBottom:2}}>Keterangan</div>{job.description}
        </div>}
        {job.status === "BATAL" && <div style={{marginTop:8, padding:"6px 8px", background:"#fee2e2", borderRadius:6, fontSize:12, color:"#991b1b"}}>
          <b>Dibatalkan:</b> {job.cancel_reason} · oleh {job.cancelled_by} · {fmtDate(job.cancelled_at)}
        </div>}
        {/* Iter 287g — Banner LOCKED yang menonjol saat state=sent. */}
        {isLocked && <div className="shopee-lock-banner"
          data-testid="shopee-locked-banner">
          <WarehouseIcon size={18}/>
          <div style={{flex:1, minWidth:200}}>
            <div style={{fontWeight:800, letterSpacing:"0.3px"}}>Terkunci — sudah dikirim ke Gudang</div>
            <div className="muted small" style={{marginTop:2}}>
              Dikirim {fmtDate(job.warehouse_sent_at)} oleh <b>{job.warehouse_sent_by}</b>
              {job.warehouse_process_state === "processing" && <> · <span style={{color:"#b91c1c", fontWeight:700}}>Gudang sedang memproses</span></>}
              . Edit / hapus / paste / status / PIC / batal <b>tidak dapat dilakukan</b> dari modul ini. Preview, unduh, cetak, dan riwayat tetap terbuka.
            </div>
          </div>
        </div>}
        {/* Iter 287v — Pekerjaan sudah dikirim namun BELUM diproses gudang: info + hint edit. */}
        {job.warehouse_state === "sent" && !isLocked && <div className="shopee-info-banner" data-testid="shopee-sent-queued-banner">
          <WarehouseIcon size={16}/>
          <div style={{flex:1, minWidth:200}}>
            <b>Dikirim ke Gudang · menunggu diproses</b>
            <div className="muted small" style={{marginTop:2}}>
              Edit & revisi masih diperbolehkan sampai Gudang klik "Proses Pesanan". Perubahan otomatis mengembalikan status ke REVISI.
            </div>
            {job.warehouse_designer_updated_at && <div style={{marginTop:4, fontSize:12, color:"#92400e"}}>
              <b>Diperbarui oleh desainer</b> · {fmtDate(job.warehouse_designer_updated_at)}
              {job.warehouse_designer_updated_by && <> oleh {job.warehouse_designer_updated_by}</>}
            </div>}
          </div>
        </div>}
        {/* Iter 287v — Pengajuan aktif (menunggu keputusan Gudang) */}
        {pendingReq && <div className="shopee-pending-banner" data-testid="shopee-pending-banner">
          <AlertCircle size={18}/>
          <div style={{flex:1, minWidth:200}}>
            <div style={{fontWeight:800}}>
              Pengajuan {pendingReq.kind === "hold" ? "Tahan/Revisi" : "Batal"} menunggu keputusan Gudang
            </div>
            <div className="muted small" style={{marginTop:2}}>
              Diajukan {fmtDate(pendingReq.requested_at)} oleh <b>{pendingReq.requested_by_name}</b>
              · <b>Alasan:</b> {pendingReq.reason}
              {pendingReq.details && <> · <b>Detail:</b> {pendingReq.details}</>}
            </div>
          </div>
        </div>}
        {/* Iter 287v — DITAHAN (setelah pengajuan hold disetujui) */}
        {isOnHold && <div className="shopee-hold-banner" data-testid="shopee-hold-banner">
          <AlertCircle size={18}/>
          <div style={{flex:1, minWidth:200}}>
            <div style={{fontWeight:800}}>Pekerjaan DITAHAN — silakan revisi & kirim ulang</div>
            <div className="muted small" style={{marginTop:2}}>
              Ditahan sejak {fmtDate(job.warehouse_hold_at)} oleh {job.warehouse_hold_by}. Setelah revisi selesai, ubah status ke FIX dan klik <b>Kirim Revisi ke Gudang</b>.
            </div>
          </div>
        </div>}
        {/* State non-locked (recalled/cancelled): panel informasi ringan. */}
        {job.warehouse_state && !["sent","on_hold"].includes(job.warehouse_state) && <div style={{marginTop:8, padding:"6px 10px", borderRadius:6, fontSize:12, display:"flex", gap:8, alignItems:"center", flexWrap:"wrap",
          background: job.warehouse_state === "recalled" ? "#fef3c7" : "#f1f5f9",
          border: `1px solid ${job.warehouse_state === "recalled" ? "#f59e0b" : "#cbd5e1"}`,
          color: job.warehouse_state === "recalled" ? "#92400e" : "#475569"}}
          data-testid={`shopee-warehouse-info-${job.warehouse_state}`}>
          <WarehouseIcon size={14}/>
          <b>
            {job.warehouse_state === "recalled" && "Kartu Gudang ditarik kembali"}
            {job.warehouse_state === "cancelled" && "Kartu Gudang: BATAL"}
          </b>
          {job.warehouse_recalled_at && <span>· {fmtDate(job.warehouse_recalled_at)} oleh {job.warehouse_recalled_by} · alasan: {job.warehouse_recall_reason}</span>}
        </div>}
      </div>

      {isProcessingLocked && job.mockup_printed_at && <div className="card" style={{padding:8, background:"#fef3c7", color:"#92400e", fontSize:13}}
        data-testid="shopee-detail-reprint-warning">Mockup sudah pernah dicetak Gudang — revisi akan membuat Gudang perlu CETAK ULANG versi baru.</div>}
      <MockupsSection job={job} canEdit={canEditMockup}
        canEditReason={job.status === "BATAL" ? "Pekerjaan BATAL — Ganti File dinonaktifkan." : ""}
        onChanged={load}/>

      <HistorySection job={job}/>
    </div>}

    {action === "batal" && <CancelDialog onClose={()=>setAction("")} onConfirm={async (reason) => {
      await setStatus("BATAL", reason); setAction("");
    }}/>}
    {action === "transfer" && <TransferDialog designers={designers.filter(d => d.id !== job?.pic_designer_id)}
      onClose={()=>setAction("")} onConfirm={async (picId, note) => {
        try { await axios.post(`${API}/shopee/${jobId}/transfer-pic`, { pic_designer_id: picId, note }); await load(); setAction(""); }
        catch (e) { setErr(e?.response?.data?.detail || "Gagal pindah desainer"); }
      }}/>}
    {/* Iter 287v — Dialog pengajuan Tahan/Batal untuk pekerjaan LOCKED. */}
    {(action === "request_hold" || action === "request_cancel") && <PendingRequestDialog
      kind={action === "request_hold" ? "hold" : "cancel"}
      onClose={()=>setAction("")}
      onConfirm={async (reason, details) => {
        try {
          await axios.post(`${API}/shopee/${jobId}/pending-request`, {
            kind: action === "request_hold" ? "hold" : "cancel", reason, details,
          });
          setAction("");
          await load();
          notify.success({ title: "Pengajuan terkirim",
            message: "Menunggu keputusan Gudang. Kartu tetap LOCKED sampai disetujui." });
        } catch (e) {
          setErr(e?.response?.data?.detail || "Gagal mengirim pengajuan");
          throw e;
        }
      }}/>}
    {/* Iter 287g — Dialog Tarik Kembali dari Gudang (Owner/Supervisor). */}
    {recallOpen && <RecallDialog job={job}
      onClose={()=>setRecallOpen(false)}
      onConfirm={async (reason) => {
        try {
          await axios.post(`${API}/shopee/${jobId}/recall-from-warehouse`, { reason });
          setRecallOpen(false);
          await load();
        } catch (e) {
          setErr(e?.response?.data?.detail || "Gagal menarik kembali");
          throw e;
        }
      }}/>}
  </ModalShell>;
}


// Iter 287g — Modal konfirmasi Tarik Kembali dari Gudang.
function RecallDialog({ job, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const processing = job?.warehouse_process_state === "processing";
  const save = async () => {
    if (reason.trim().length < 3) return;
    setBusy(true); setErr("");
    try { await onConfirm(reason.trim()); }
    catch (e) { setErr(e?.response?.data?.detail || "Gagal menarik"); }
    finally { setBusy(false); }
  };
  return <ModalShell title="Tarik Kembali dari Gudang" onClose={onClose} testid="shopee-recall-modal">
    <div style={{display:"flex", flexDirection:"column", gap:8}}>
      {processing
        ? <div style={{padding:"8px 10px", background:"#fee2e2", border:"1px solid #ef4444", borderRadius:6, fontSize:12, color:"#991b1b"}} data-testid="shopee-recall-processing-warning">
            <b>Gudang sudah mulai memproses kartu ini.</b> Penarikan otomatis tidak lagi tersedia — silakan koordinasi langsung dengan tim Gudang untuk membatalkan cetak.
          </div>
        : <div className="muted small">Kartu akan keluar dari antrean Gudang, kunci dibuka, dan status desain otomatis menjadi <b>REVISI</b>. Pengiriman berikutnya memperbarui pekerjaan yang sama.</div>}
      <label style={{fontSize:12, fontWeight:600}}>Alasan penarikan <span style={{color:"#b91c1c"}}>*</span></label>
      <textarea value={reason} onChange={e=>setReason(e.target.value)} rows={3}
        placeholder="Contoh: salah warna, salah judul, revisi pelanggan mendadak"
        style={{...inputStyle, resize:"vertical"}}
        disabled={processing}
        data-testid="shopee-recall-reason"/>
      {err && <div className="warn-box small" data-testid="shopee-recall-error">{err}</div>}
      <div style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
        <button className="soft-btn small-btn" onClick={onClose} disabled={busy}>Tutup</button>
        <button className="primary small-btn"
          style={{background:"#b91c1c", borderColor:"#b91c1c"}}
          onClick={save}
          disabled={busy || processing || reason.trim().length < 3}
          data-testid="shopee-recall-submit">
          {busy ? "Memproses…" : "Ya, Tarik Kembali"}
        </button>
      </div>
    </div>
  </ModalShell>;
}


function MockupsSection({ job, canEdit, canEditReason = "", onChanged }) {
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const fileNewRef = useRef(null);
  // Iter 287d — Paste Mockup: buffer file dari clipboard + preview modal sebelum upload.
  const [pasted, setPasted] = useState(null); // { file, url }

  const precheckUrl = `${API}/shopee/${job.id}/mockups/precheck`;
  const uploadNew = async (file, title = "", fmt = "A5L", g = {}) => {
    if (!file) return;
    setUploading(true); setUploadErr("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const params = new URLSearchParams({ paper_format: fmt, ...g }); if (title) params.set("title", title);
      showOrderNoCheck(await axios.post(`${API}/shopee/${job.id}/mockups?${params.toString()}`, fd, { headers:{"Content-Type":"multipart/form-data"} }));
      await onChanged();
    } catch (e) { setUploadErr(formatApiError(e, "Gagal mengunggah mockup baru.")); }
    finally { setUploading(false); }
  };

  // Iter 287ah — Ganti File (revisi mockup). Kembalikan boolean sukses agar tile bisa
  // mengetahui hasil upload (untuk reset state lokal). Error dinormalisasi via
  // formatApiError agar tidak pernah merender objek mentah ke React.
  const uploadVersion = async (mockupId, file, note = "", fmt = "A5L", g = {}) => {
    if (!file) return false;
    setUploading(true); setUploadErr("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const params = new URLSearchParams({ paper_format: fmt, ...g }); if (note) params.set("note", note);
      showOrderNoCheck(await axios.post(`${API}/shopee/${job.id}/mockups/${mockupId}/versions?${params.toString()}`, fd, { headers:{"Content-Type":"multipart/form-data"} }));
      await onChanged();
      return true;
    } catch (e) {
      setUploadErr(formatApiError(e, "Gagal mengunggah file pengganti."));
      return false;
    } finally { setUploading(false); }
  };

  // Ganti File dari tile: lewat modal pengaman (pratinjau + tujuan + peringatan).
  const replaceVersion = async (mockupId, file, note = "") => {
    if (!file) return false;
    setUploadErr("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const base = { paper_format: jobPaper(job), ...(note ? { note } : {}) };
      const res = await guardedUpload({ precheckUrl, file, send: (g) => {
        setUploading(true);
        return axios.post(withQuery(`${API}/shopee/${job.id}/mockups/${mockupId}/versions`, { ...base, ...g }), fd,
          { headers:{"Content-Type":"multipart/form-data"} }).finally(() => setUploading(false));
      } });
      if (!res) return false;
      await onChanged();
      return true;
    } catch (e) {
      const msg = formatApiError(e, "Gagal mengunggah file pengganti.");
      setUploadErr(msg); notify.error({ title: "Ganti File gagal", message: msg });
      return false;
    }
  };

  // Iter 287o — Hapus (soft) satu mockup slot. Backend mengunci bila sudah dikirim.
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, title }
  const [deleting, setDeleting] = useState(false);
  const doDeleteMockup = async () => {
    if (!confirmDelete) return;
    setDeleting(true); setUploadErr("");
    try {
      await axios.delete(`${API}/shopee/${job.id}/mockups/${confirmDelete.id}`);
      setConfirmDelete(null);
      await onChanged();
    } catch (e) {
      const msg = formatApiError(e, "Gagal menghapus mockup.");
      setUploadErr(msg); setConfirmDelete(null); notify.error({ title: "Hapus mockup gagal", message: msg });
    } finally { setDeleting(false); }
  };

  // Iter 287d — Listener Ctrl+V / Cmd+V untuk gambar clipboard. Aktif hanya bila detail terbuka
  // (komponen ini di-unmount saat modal ditutup), dan hanya bila `canEdit` (status != BATAL).
  // Skip bila fokus ada di input/textarea/contentEditable supaya paste teks tetap berfungsi.
  useEffect(() => {
    if (!canEdit) return;
    const onPaste = (e) => {
      const ae = document.activeElement;
      const tag = (ae?.tagName || "").toUpperCase();
      const isEditable = tag === "INPUT" || tag === "TEXTAREA" || ae?.isContentEditable;
      if (isEditable) return;
      const items = e.clipboardData?.items || [];
      let file = null;
      for (const it of items) {
        if (it.kind === "file" && (it.type || "").startsWith("image/")) {
          file = it.getAsFile();
          if (file) break;
        }
      }
      if (!file) return;
      e.preventDefault();
      const url = URL.createObjectURL(file);
      setPasted({ file, url });
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [canEdit]);

  // Cleanup blob URL ketika pasted dibuang / ganti.
  useEffect(() => () => { if (pasted?.url) URL.revokeObjectURL(pasted.url); }, [pasted]);

  // Iter 287e — Tombol "Tempel dari Clipboard" — baca gambar clipboard on-demand via
  // navigator.clipboard.read() (dibutuhkan di HP karena Ctrl+V tidak tersedia).
  // Menggunakan modal preview yang sama dengan paste keyboard.
  const readClipboardImage = async () => {
    setUploadErr("");
    if (!navigator.clipboard || typeof navigator.clipboard.read !== "function") {
      setUploadErr("Browser tidak mendukung akses clipboard gambar. Silakan gunakan tombol 'Tambah Mockup' untuk mengunggah gambar.");
      return;
    }
    try {
      const items = await navigator.clipboard.read();
      let file = null;
      for (const it of items) {
        const type = (it.types || []).find(t => t.startsWith("image/"));
        if (!type) continue;
        const blob = await it.getType(type);
        const ext = type.split("/")[1] || "png";
        file = new File([blob], `clipboard-${Date.now()}.${ext}`, { type });
        break;
      }
      if (!file) {
        setUploadErr("Clipboard tidak berisi gambar. Salin gambar terlebih dahulu (klik kanan → Salin gambar), lalu coba lagi.");
        return;
      }
      const url = URL.createObjectURL(file);
      setPasted({ file, url });
    } catch (e) {
      const name = e?.name || "";
      if (name === "NotAllowedError") {
        setUploadErr("Izin clipboard ditolak. Izinkan akses clipboard di pengaturan browser, atau gunakan 'Tambah Mockup' untuk unggah biasa.");
      } else if (name === "SecurityError") {
        setUploadErr("Akses clipboard hanya tersedia di koneksi HTTPS. Gunakan 'Tambah Mockup' untuk unggah biasa.");
      } else {
        setUploadErr("Gagal membaca clipboard: " + (e?.message || name || "unknown") + ". Gunakan 'Tambah Mockup' untuk unggah biasa.");
      }
    }
  };

  return <div className="card" style={{padding:10}}>
    <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8, flexWrap:"wrap", gap:6}}>
      <div style={{fontWeight:700}}>Mockup ({(job.mockups || []).filter(m => !m.deleted_at).length}) <span className="muted small" style={{fontWeight:400}}>· Tempel gambar (Ctrl+V) juga bisa</span></div>
      {canEdit && <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
        <input ref={fileNewRef} type="file" accept="image/*,application/pdf" hidden
          onChange={e=>{
            const f = e.target.files?.[0];
            e.target.value = "";
            if (!f) return;
            // Iter 287n — Ganti window.prompt dengan modal in-app (PastePreviewModal)
            // yang menampilkan preview + kolom Judul opsional + Simpan/Batal.
            const url = URL.createObjectURL(f);
            setPasted({ file: f, url, source: "pick" });
          }}
          data-testid="shopee-mockup-upload-input"/>
        <button className="primary small-btn" onClick={()=>fileNewRef.current?.click()} disabled={uploading}
          data-testid="shopee-mockup-add-btn"><Upload size={14}/> Tambah Mockup</button>
        <button className="soft-btn small-btn" onClick={readClipboardImage} disabled={uploading}
          data-testid="shopee-mockup-paste-btn"
          title="Baca gambar dari clipboard (Ctrl+V juga bisa saat detail terbuka)">
          <ClipboardPaste size={14}/> Tempel dari Clipboard
        </button>
      </div>}
    </div>
    {uploadErr && <div className="warn-box small" data-testid="shopee-mockup-error">{uploadErr}</div>}
    {(() => {
      // Iter 287o — Filter mockup yang sudah dihapus (soft-delete) dari daftar aktif.
      const activeMockups = (job.mockups || []).filter(m => !m.deleted_at);
      const deletedCount = (job.mockups || []).length - activeMockups.length;
      return <>
        {activeMockups.length === 0 && <div className="muted small">Belum ada mockup{deletedCount ? " aktif" : ""}.</div>}
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(200px, 1fr))", gap:10, marginTop:6}}>
          {activeMockups.map(m => <MockupTile key={m.id} mockup={m} canEdit={canEdit}
            canEditReason={canEditReason}
            onNewVersion={(f, note)=>replaceVersion(m.id, f, note)}
            onRequestDelete={() => setConfirmDelete({ id: m.id, title: m.title })}
            onSetFormat={async (fileId, fmt) => {
              try { await setPaperFormat([fileId], fmt); await onChanged(); }
              catch (e) { setUploadErr(formatApiError(e, "Gagal menyimpan format kertas.")); }
            }}
            uploading={uploading}/>)}
        </div>
      </>;
    })()}
    {pasted && <PastePreviewModal
      preview={pasted} defaultFormat={jobPaper(job)}
      mockups={(job.mockups || []).filter(m => !m.deleted_at)}
      uploading={uploading}
      precheckUrl={precheckUrl}
      onCancel={()=>setPasted(null)}
      onSaveNew={async (title, fmt, g) => { await uploadNew(pasted.file, title, fmt, g); setPasted(null); }}
      onSaveVersion={async (mockupId, note, fmt, g) => { await uploadVersion(mockupId, pasted.file, note, fmt, g); setPasted(null); }}
    />}
    {confirmDelete && <ConfirmDeleteMockupModal
      title={confirmDelete.title}
      busy={deleting}
      onCancel={() => !deleting && setConfirmDelete(null)}
      onConfirm={doDeleteMockup}
    />}
  </div>;
}

// Iter 287o — Modal konfirmasi hapus mockup (tema oranye Shopee).
function ConfirmDeleteMockupModal({ title, busy, onCancel, onConfirm }) {
  return <ModalShell title="Hapus mockup ini?" onClose={onCancel} testid="shopee-mockup-delete-modal">
    <div style={{display:"flex", flexDirection:"column", gap:10}}>
      <div style={{fontSize:13}}>
        Mockup <b>{title || "(tanpa judul)"}</b> akan dihapus dari daftar aktif, pratinjau, dan cetak.
        Riwayat versi tetap tersimpan sebagai jejak audit. Versi lama <b>tidak</b> otomatis menjadi aktif.
      </div>
      <div className="muted small">
        Bila status sekarang FIX, akan otomatis berubah menjadi REVISI. Kirim ke Gudang tetap dikunci sampai ada mockup aktif kembali.
      </div>
      <div className="shp-modal-actions" style={{display:"flex", gap:8, justifyContent:"flex-end", marginTop:6}}>
        <button className="soft-btn small-btn" onClick={onCancel} disabled={busy}
          data-testid="shopee-mockup-delete-cancel">Batal</button>
        <button className="primary small-btn"
          style={{background:"#b91c1c", borderColor:"#b91c1c"}}
          onClick={onConfirm} disabled={busy}
          data-testid="shopee-mockup-delete-confirm">
          {busy ? "Menghapus…" : "Hapus"}
        </button>
      </div>
    </div>
  </ModalShell>;
}

// Iter 287d — Modal preview gambar hasil paste sebelum di-upload.
// Pilihan: simpan sebagai mockup baru (default) ATAU sebagai revisi dari mockup existing.
// Aturan FIX→REVISI otomatis mengikuti alur server; tidak ada logika baru di sini.
function PastePreviewModal({ preview, mockups, uploading, precheckUrl, onCancel, onSaveNew, onSaveVersion, defaultFormat = "A5L" }) {
  const savingRef = useRef(false);
  const pc = usePrecheck(precheckUrl, preview.file);
  const [ack, setAck] = useState(false);
  const [reason, setReason] = useState("");
  const guardOk = pc.status === "ok" && canProceed(pc.check, ack, reason);
  const [mode, setMode] = useState("new"); // "new" | "version"
  const [title, setTitle] = useState(() => {
    // Iter 287n — Dari pilih file: default kosong (judul opsional).
    // Dari paste clipboard (lama): default "Paste HH:MM".
    if (preview?.source === "pick") return "";
    const d = new Date();
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `Paste ${hh}:${mm}`;
  });
  const [mockupId, setMockupId] = useState(mockups[0]?.id || "");
  const [note, setNote] = useState("");
  const [fmt, setFmt] = useState(defaultFormat);
  const canSave = !uploading && guardOk && (mode === "new" ? true : !!mockupId);
  const save = async () => {
    if (!canSave || savingRef.current) return;  // cegah unggahan ganda (klik/Enter beruntun)
    savingRef.current = true;
    const g = guardParams(pc.check, ack, reason);
    try {
      if (mode === "new") await onSaveNew(title.trim(), fmt, g);
      else await onSaveVersion(mockupId, note.trim(), fmt, g);
    } finally { savingRef.current = false; }
  };
  const isImg = (preview.file?.type || "").startsWith("image");
  const modalTitle = preview?.source === "pick" ? "Simpan Mockup" : "Simpan Mockup dari Clipboard";
  return <ModalShell title={modalTitle} onClose={onCancel} testid="shopee-paste-modal" wide>
    <div style={{display:"flex", flexDirection:"column", gap:10}}>
      <div style={{background:"var(--tf-neutral-bg)", borderRadius:6, padding:8, textAlign:"center", border:"1px solid var(--tf-neutral-border)"}}>
        {isImg
          ? <img src={preview.url} alt="mockup pratinjau"
              style={{maxWidth:"100%", maxHeight:360, objectFit:"contain", borderRadius:4}}
              data-testid="shopee-paste-preview"/>
          : <div className="muted small" style={{padding:24}} data-testid="shopee-paste-preview-nonimage">
              Bukan gambar — pratinjau tidak tersedia.<br/>
              <b>{preview.file?.name || "file"}</b>
            </div>}
        <div className="muted small" style={{marginTop:4}}>{Math.round((preview.file.size || 0)/1024)} KB · {preview.file.type || "file"}</div>
      </div>
      <GuardPanel pc={pc} ack={ack} setAck={setAck} reason={reason} setReason={setReason}/>
      <div style={{display:"flex", gap:12, flexWrap:"wrap"}}>
        <label style={{display:"flex", gap:6, alignItems:"center", cursor:"pointer", fontSize:13}}>
          <input type="radio" checked={mode==="new"} onChange={()=>setMode("new")}
            data-testid="shopee-paste-mode-new"/> Sebagai mockup baru
        </label>
        <label style={{display:"flex", gap:6, alignItems:"center", cursor: mockups.length ? "pointer" : "not-allowed", fontSize:13, opacity: mockups.length ? 1 : 0.5}}>
          <input type="radio" checked={mode==="version"} onChange={()=>setMode("version")}
            disabled={!mockups.length}
            data-testid="shopee-paste-mode-version"/> Sebagai revisi dari mockup existing
        </label>
      </div>
      {mode === "new" && <div>
        <label style={{fontSize:12, fontWeight:600}}>Judul mockup <span className="muted" style={{fontWeight:400}}>(opsional)</span></label>
        <input value={title} onChange={e=>setTitle(e.target.value)} style={inputStyle}
          placeholder="Contoh: Depan / Belakang / Detail — boleh kosong"
          data-testid="shopee-paste-title"/>
      </div>}
      {mode === "version" && <div style={{display:"flex", flexDirection:"column", gap:6}}>
        <label style={{fontSize:12, fontWeight:600}}>Mockup tujuan</label>
        <select value={mockupId} onChange={e=>setMockupId(e.target.value)} style={inputStyle}
          data-testid="shopee-paste-mockup">
          {mockups.map(m => <option key={m.id} value={m.id}>{m.title} (v{m.current_version})</option>)}
        </select>
        <label style={{fontSize:12, fontWeight:600}}>Catatan revisi (opsional)</label>
        <input value={note} onChange={e=>setNote(e.target.value)} style={inputStyle}
          placeholder="Contoh: perbaiki logo"
          data-testid="shopee-paste-note"/>
      </div>}
      <div>
        <label style={{fontSize:12, fontWeight:600}}>Format kertas cetak</label>
        <PaperFormatToggle value={fmt} onChange={setFmt} disabled={uploading} tid="shopee-paste-format"/>
      </div>
      <div className="shp-modal-actions" style={{display:"flex", gap:8, justifyContent:"flex-end", marginTop:6}}>
        <button className="soft-btn small-btn" onClick={onCancel} disabled={uploading}
          data-testid="shopee-paste-cancel">Batal</button>
        <button className="primary small-btn" onClick={save} disabled={!canSave}
          data-testid="shopee-paste-save">
          {uploading ? "Mengunggah…" : pc.check?.needs_confirm ? "Konfirmasi & Simpan" : "Simpan"}
        </button>
      </div>
    </div>
  </ModalShell>;
}

function MockupTile({ mockup, canEdit, canEditReason, onNewVersion, onRequestDelete, onSetFormat, uploading }) {
  const [showAll, setShowAll] = useState(false);
  // Iter 287ah — busy per-tile: hanya tile ini yang tampil "Mengunggah…" saat sedang upload
  // versi baru. Mencegah klik ganda dan memberi feedback yang jelas ke user.
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const cur = mockup.versions.find(v => v.version === mockup.current_version) || mockup.versions[mockup.versions.length - 1];
  const previewUrl = cur && (cur.content_type || "").startsWith("image") ? `${API}/shopee/files/${cur.file_id}/preview` : null;
  const disabledReplace = busy || uploading;
  // Iter 287ah — iOS Safari kadang tidak konsisten memicu <input hidden>.click().
  // Gunakan CSS positioning agar input tetap ada di layout tapi tak terlihat & tak menerima tap sendiri.
  const hiddenInputStyle = { position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none", overflow: "hidden" };
  const handleReplaceClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabledReplace) return;
    // Sinkron langsung dari user gesture — wajib untuk iOS Safari.
    fileRef.current?.click();
  };
  return <div className="card" style={{padding:0, overflow:"hidden"}} data-testid={`shopee-mockup-${mockup.id}`}>
    <div style={{aspectRatio:"1", background:"#f1f5f9", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden"}}>
      {previewUrl
        ? <img src={previewUrl} alt={mockup.title} style={{width:"100%", height:"100%", objectFit:"cover"}}/>
        : <div className="muted small" style={{padding:12, textAlign:"center"}}>Bukan gambar<br/>({cur?.filename})</div>}
    </div>
    <div style={{padding:8}}>
      <div style={{fontWeight:700, fontSize:13, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}}>{mockup.title}</div>
      <div className="muted small" style={{fontSize:11}}>Versi {mockup.current_version} · {fmtDate(cur?.uploaded_at)}</div>
      {cur && <div style={{marginTop:6}}>
        <FormatBadge value={cur.paper_format} tid={`shopee-mockup-format-${mockup.id}`}/>
        {onSetFormat && <PaperFormatToggle value={cur.paper_format} disabled={busy || uploading}
          onChange={async (f) => { if (f === cur.paper_format) return; setBusy(true); try { await onSetFormat(cur.file_id, f); } finally { setBusy(false); } }}
          tid={`shopee-mockup-format-set-${mockup.id}`}/>}
      </div>}
      {cur?.file_id && <MockupAiRead fileId={cur.file_id}/>}
      <div style={{display:"flex", gap:4, marginTop:6, flexWrap:"wrap", alignItems:"center"}}>
        {previewUrl && <a href={previewUrl} target="_blank" rel="noreferrer"
          className="soft-btn small-btn" data-testid={`shopee-mockup-preview-${mockup.id}`}>Pratinjau</a>}
        {cur && <a href={`${API}/shopee/files/${cur.file_id}/download`} target="_blank" rel="noreferrer"
          className="soft-btn small-btn" data-testid={`shopee-mockup-download-${mockup.id}`}>Unduh</a>}
        {canEdit && <>
          {/* Iter 287ah — Input file selalu ter-render (bukan `hidden`) agar tap→click konsisten di iOS Safari. */}
          <input ref={fileRef} type="file" accept="image/*,application/pdf"
            style={hiddenInputStyle}
            tabIndex={-1} aria-hidden="true"
            onChange={async e => {
              const f = e.target.files?.[0];
              // Reset value dulu agar file yang sama bisa dipilih ulang nanti.
              e.target.value = "";
              if (!f) return; // Dibatalkan → tidak mengubah apapun.
              if (busy || uploading) return;
              setBusy(true);
              try {
                await onNewVersion(f, "");
              } finally {
                setBusy(false);
              }
            }}
            data-testid={`shopee-mockup-version-input-${mockup.id}`}/>
          <button type="button" className="soft-btn small-btn" onClick={handleReplaceClick}
            disabled={disabledReplace}
            title="Pilih file pengganti untuk mockup ini (versi baru)"
            data-testid={`shopee-mockup-version-btn-${mockup.id}`}>
            {busy ? "Mengunggah…" : "Ganti File"}
          </button>
          {onRequestDelete && <button
            type="button"
            className="soft-btn small-btn"
            onClick={() => onRequestDelete()}
            disabled={disabledReplace}
            style={{color:"#b91c1c", borderColor:"#fecaca", background:"#fef2f2"}}
            data-testid={`shopee-mockup-delete-btn-${mockup.id}`}
            title="Hapus mockup ini">Hapus</button>}
        </>}
        {!canEdit && canEditReason && <span className="muted small" style={{fontSize:11, fontStyle:"italic"}}
          data-testid={`shopee-mockup-locked-hint-${mockup.id}`}>{canEditReason}</span>}
        {mockup.versions.length > 1 && <button type="button" className="link-btn small-btn" onClick={()=>setShowAll(v=>!v)}
          data-testid={`shopee-mockup-history-btn-${mockup.id}`}>{showAll ? "Sembunyikan" : `Riwayat (${mockup.versions.length})`}</button>}
      </div>
      {showAll && <div style={{marginTop:6, borderTop:"1px solid #e2e8f0", paddingTop:6, display:"flex", flexDirection:"column", gap:3, fontSize:11}}>
        {mockup.versions.slice().reverse().map(v => <div key={v.version} style={{display:"flex", justifyContent:"space-between", gap:4}}>
          <span><b>v{v.version}</b>{v.version === mockup.current_version ? " (terbaru)" : ""}</span>
          <span className="muted">{fmtDate(v.uploaded_at)} · {v.uploaded_by_name}</span>
        </div>)}
      </div>}
    </div>
  </div>;
}

function HistorySection({ job }) {
  const [open, setOpen] = useState(false);
  const h = (job.history || []).slice().reverse();
  return <div className="card" style={{padding:10}}>
    <button className="link-btn" onClick={()=>setOpen(o=>!o)} data-testid="shopee-history-toggle">
      {open ? "Sembunyikan" : `Riwayat (${h.length})`}
    </button>
    {open && <div style={{marginTop:8, display:"flex", flexDirection:"column", gap:4, fontSize:12}}>
      {h.map(e => <div key={e.id} style={{padding:"6px 8px", background:"#f8fafc", borderRadius:4, borderLeft:"2px solid #cbd5e1"}}>
        <div style={{display:"flex", justifyContent:"space-between", gap:8, flexWrap:"wrap"}}>
          <b>{e.event}</b>
          <span className="muted small">{fmtDate(e.at)} · {e.by_name}</span>
        </div>
        {e.note && <div style={{marginTop:2}}>{e.note}</div>}
      </div>)}
    </div>}
  </div>;
}

// Iter 287v — Dialog pengajuan Tahan/Batal untuk pekerjaan LOCKED.
function PendingRequestDialog({ kind, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const isHold = kind === "hold";
  const title = isHold ? "Ajukan Tahan / Revisi ke Gudang" : "Ajukan Batal ke Gudang";
  return <ModalShell title={title} onClose={onClose} testid={`shopee-pending-modal-${kind}`}>
    <div style={{display:"flex", flexDirection:"column", gap:8}}>
      <div className="muted small">
        {isHold
          ? "Gudang akan menghentikan proses fisik setelah menyetujui pengajuan Anda. Kartu tidak langsung terbuka."
          : "Pengajuan Batal harus disetujui Gudang. Jika disetujui, pekerjaan tidak dapat dikembalikan."}
      </div>
      <label style={{fontSize:12, fontWeight:600}}>Alasan <span style={{color:"#b91c1c"}}>*</span></label>
      <textarea value={reason} onChange={e=>setReason(e.target.value)} rows={2}
        placeholder="Contoh: pelanggan minta ganti ukuran"
        style={{...inputStyle, resize:"vertical"}} data-testid={`shopee-pending-reason-${kind}`}/>
      <label style={{fontSize:12, fontWeight:600}}>Rincian perubahan (opsional)</label>
      <textarea value={details} onChange={e=>setDetails(e.target.value)} rows={3}
        placeholder="Contoh: ukuran M → L, warna hitam → navy"
        style={{...inputStyle, resize:"vertical"}} data-testid={`shopee-pending-details-${kind}`}/>
      <div className="shp-modal-actions" style={{display:"flex", gap:8, justifyContent:"flex-end", marginTop:6}}>
        <button className="soft-btn small-btn" onClick={onClose} disabled={busy}
          data-testid={`shopee-pending-cancel-${kind}`}>Batal</button>
        <button className={`primary small-btn ${isHold ? "tf-btn-warning" : "tf-btn-danger"}`}
          onClick={async ()=>{
            setBusy(true);
            try { await onConfirm(reason.trim(), details.trim()); }
            catch { /* keep dialog open on error */ }
            finally { setBusy(false); }
          }}
          disabled={busy || reason.trim().length < 3}
          data-testid={`shopee-pending-submit-${kind}`}>
          {busy ? "Mengirim…" : (isHold ? "Ajukan Tahan/Revisi" : "Ajukan Batal")}
        </button>
      </div>
    </div>
  </ModalShell>;
}


function CancelDialog({ onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  // Iter 287am — Chip preset alasan pembatalan cepat.
  const PRESETS = [
    "Pelanggan batal",
    "Salah desain",
    "Tidak jadi transaksi",
    "Bahan tidak tersedia",
    "Duplikat pesanan",
  ];
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm(reason.trim());
    } catch (_) { /* pesan sudah dihandle di parent lewat notify.error */ }
    finally { setBusy(false); }
  };
  return <ModalShell title="Batalkan Pekerjaan"
    onClose={busy ? undefined : onClose}
    testid="shopee-cancel-modal">
    <div style={{display:"flex", flexDirection:"column", gap:8}}>
      <div className="muted small">Alasan pembatalan wajib. Data & riwayat tetap tersimpan.</div>
      <div className="shopee-cancel-presets" data-testid="shopee-cancel-presets">
        {PRESETS.map(p => <button type="button" key={p}
          className={`shopee-cancel-chip${reason.trim() === p ? " is-active" : ""}`}
          onClick={()=>setReason(p)}
          disabled={busy}
          data-testid={`shopee-cancel-preset-${p.replace(/\s+/g,"-").toLowerCase()}`}>
          {p}
        </button>)}
      </div>
      <textarea value={reason} onChange={e=>setReason(e.target.value)} rows={3}
        placeholder="Atau ketik alasan lain di sini…"
        style={{...inputStyle, resize:"vertical"}} data-testid="shopee-cancel-reason"
        disabled={busy}/>
      <div style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
        <button type="button" className="soft-btn small-btn" onClick={onClose} disabled={busy}
          data-testid="shopee-cancel-back">Kembali</button>
        <button type="button" className="primary small-btn"
          style={{background:"#b91c1c", borderColor:"#b91c1c", boxShadow:"none"}}
          onClick={submit}
          disabled={busy || reason.trim().length < 3}
          data-testid="shopee-cancel-submit">
          {busy ? "Memproses…" : "Batalkan Pesanan"}
        </button>
      </div>
    </div>
  </ModalShell>;
}

function TransferDialog({ designers, onClose, onConfirm }) {
  const [picId, setPicId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  return <ModalShell title="Pindahkan Desainer" onClose={onClose} testid="shopee-transfer-modal">
    <div style={{display:"flex", flexDirection:"column", gap:8}}>
      <div className="muted small">Pekerjaan tetap sama; riwayat perpindahan tercatat.</div>
      <label style={{fontSize:12, fontWeight:600}}>Desainer baru</label>
      <select value={picId} onChange={e=>setPicId(e.target.value)} style={inputStyle}
        data-testid="shopee-transfer-pic">
        <option value="">— pilih —</option>
        {designers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
      </select>
      <label style={{fontSize:12, fontWeight:600}}>Catatan (opsional)</label>
      <input value={note} onChange={e=>setNote(e.target.value)} style={inputStyle}
        placeholder="Alasan pindah desainer"
        data-testid="shopee-transfer-note"/>
      <div style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
        <button className="soft-btn small-btn" onClick={onClose} disabled={busy}>Tutup</button>
        <button className="primary small-btn" onClick={async ()=>{ setBusy(true); await onConfirm(picId, note); setBusy(false); }}
          disabled={busy || !picId}
          data-testid="shopee-transfer-submit">
          {busy ? "Memproses…" : "Pindahkan"}
        </button>
      </div>
    </div>
  </ModalShell>;
}

function RekapPanel({ filters, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const params = {};
        if (filters.date_from) params.date_from = filters.date_from;
        if (filters.date_to) params.date_to = filters.date_to;
        const r = await axios.get(`${API}/shopee/rekap/summary`, { params });
        setData(r.data);
      } catch (e) { setErr(e?.response?.data?.detail || "Gagal memuat rekap"); }
    })();
  }, [filters.date_from, filters.date_to]);
  return <ModalShell title="Rekap Performa Shopee" onClose={onClose} testid="shopee-rekap-modal" wide>
    {err && <div className="warn-box small">{err}</div>}
    {!data && !err && <div className="muted small"><Loader2 size={14} className="spin"/> Memuat…</div>}
    {data && <div style={{display:"flex", flexDirection:"column", gap:12}}>
      <div className="muted small">
        Rentang: {data.period.from || "sejak awal"} — {data.period.to || "hari ini"}. Hitungan per pekerjaan (bukan per gambar).
      </div>
      {[
        {k:"incoming", l:"Pekerjaan Masuk", note:"berdasarkan tanggal masuk; kredit ke desainer awal"},
        {k:"first_fix", l:"FIX Pertama", note:"kredit ke desainer saat FIX pertama; tidak berubah walau desainer dipindah"},
        {k:"active_non_fix", l:"Aktif Belum Fix", note:"snapshot: BELUM_FIX + REVISI"},
        {k:"cancelled", l:"Batal", note:"berdasarkan tanggal batal"},
      ].map(sec => {
        const d = data[sec.k];
        return <div key={sec.k} className="card" style={{padding:10}} data-testid={`shopee-rekap-${sec.k}`}>
          <div style={{display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:8}}>
            <div>
              <div style={{fontWeight:700}}>{sec.l}</div>
              <div className="muted small">{sec.note}</div>
            </div>
            <div style={{fontSize:24, fontWeight:800}}>{d.total}</div>
          </div>
          {sec.k !== "active_non_fix" && <div className="muted small" style={{marginTop:4}}>
            Reguler: <b>{d.reguler}</b> · Lembur: <b>{d.lembur}</b>
          </div>}
          {sec.k === "active_non_fix" && <div className="muted small" style={{marginTop:4}}>
            Belum Fix: <b>{d.belum_fix}</b> · Revisi: <b>{d.revisi}</b>
          </div>}
          {d.by_designer && d.by_designer.length > 0 && <div style={{marginTop:8, borderTop:"1px solid #e2e8f0", paddingTop:6}}>
            <table style={{width:"100%", fontSize:12}}>
              <thead style={{textAlign:"left"}}>
                <tr><th>Designer</th><th style={{textAlign:"right"}}>Reguler</th><th style={{textAlign:"right"}}>Lembur</th><th style={{textAlign:"right"}}>Total</th></tr>
              </thead>
              <tbody>
                {d.by_designer.map(row => <tr key={row.designer_id}>
                  <td>{row.designer_name}</td>
                  <td style={{textAlign:"right"}}>{row.reguler || 0}</td>
                  <td style={{textAlign:"right"}}>{row.lembur || 0}</td>
                  <td style={{textAlign:"right", fontWeight:700}}>{row.total || 0}</td>
                </tr>)}
              </tbody>
            </table>
          </div>}
        </div>;
      })}
    </div>}
  </ModalShell>;
}


// --- Helpers ---
function StatusBadge({ status }) {
  const s = STATUS_LABELS[status] || STATUS_LABELS.BELUM_FIX;
  return <span className="shopee-status-badge" style={{background:s.bg, color:s.c, padding:"3px 8px", borderRadius:6, fontSize:12, fontWeight:700}}>
    <s.Icon size={12} strokeWidth={2.5} aria-hidden="true"/>
    <span className="shopee-status-text">{s.t}</span>
  </span>;
}

function ModalShell({ title, onClose, children, testid, wide = false }) {
  // Iter 287q — Kelas `shopee-theme` supaya semua modal Shopee ikut style scoped
  // (light+dark). Latar tidak lagi hardcode #fff — biarkan CSS themed yang mengatur.
  // Kolom yang difokus digeser ke tengah agar tidak tertutup keyboard HP.
  const keepVisible = e => { const t = e.target; if (t.matches?.("input, select, textarea")) setTimeout(() => t.scrollIntoView?.({ block: "center", behavior: "smooth" }), 300); };
  return <div className="shp-modal-overlay"
    onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    data-testid={testid}>
    <div className="card shopee-theme shp-modal" style={{maxWidth: wide ? 900 : 520}} role="dialog" aria-modal="true" aria-label={typeof title === "string" ? title : undefined}>
      <div className="shp-modal-head">
        <h3>{title}</h3>
        <button className="soft-btn small-btn shp-modal-close" onClick={onClose} data-testid={`${testid}-close`}>Tutup</button>
      </div>
      <div className="shp-modal-body" onFocus={keepVisible}>{children}</div>
    </div>
  </div>;
}

const inputStyle = { padding:"8px 10px", border:"1px solid #cbd5e1", borderRadius:6, fontSize:13, width:"100%" };


// --- Print HTML (server-side style, tidak perubah status) ---
function buildPrintHTML(job) {
  // Iter 287o — Cetak hanya mockup aktif; yang di-soft-delete tidak diikutkan.
  const items = (job.mockups || []).filter(m => !m.deleted_at).map(m => {
    const cur = m.versions.find(v => v.version === m.current_version) || m.versions[m.versions.length - 1];
    if (!cur) return "";
    const url = `${API}/shopee/files/${cur.file_id}/preview`;
    return `<div class="mk"><div class="mk-title">${escapeHtml(m.title)} · v${cur.version}</div>
      ${(cur.content_type || "").startsWith("image") ? `<img src="${url}" alt="${escapeHtml(m.title)}"/>` : `<div class="mk-doc">${escapeHtml(cur.filename)}</div>`}
    </div>`;
  }).join("");
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Mockup ${escapeHtml(job.order_number)}</title>
    <style>
      body{font-family: 'Plus Jakarta Sans', system-ui, sans-serif; margin:16px; color:#0f172a;}
      h1{margin:0 0 6px; font-size:20px;}
      .meta{font-size:12px; color:#475569; margin-bottom:12px;}
      .meta b{color:#0f172a;}
      .desc{font-size:12px; margin:10px 0; padding:8px; background:#f8fafc; border-radius:4px;}
      .repeat-badge{display:inline-block; margin:4px 0 10px; padding:4px 10px; border-radius:999px; background:linear-gradient(90deg,#D4AF37,#E8C656); color:#0f172a; font-size:11px; font-weight:800; letter-spacing:.04em; text-transform:uppercase; border:1px solid #B8902B;}
      .repeat-badge .ref{font-weight:600; text-transform:none; margin-left:6px;}
      .grid{display:grid; grid-template-columns:1fr 1fr; gap:12px;}
      .mk{border:1px solid #e2e8f0; border-radius:6px; padding:8px; page-break-inside:avoid;}
      .mk-title{font-size:12px; font-weight:700; margin-bottom:6px;}
      .mk img{max-width:100%; height:auto; display:block;}
      .mk-doc{padding:12px; background:#f1f5f9; text-align:center; font-size:12px;}
      @media print { body{ margin: 6mm; } }
    </style></head><body>
    <h1>Mockup Shopee — ${escapeHtml(job.order_number)}</h1>
    ${job.is_repeat_order ? `<div class="repeat-badge">⟳ Repeat Order${job.repeat_ref ? `<span class="ref">· Repeat dari Pesanan: <b>${escapeHtml(job.repeat_ref)}</b></span>` : ""}</div>` : ""}
    <div class="meta">
      Akun: <b>${escapeHtml(job.customer_account || "-")}</b> ·
      PIC: <b>${escapeHtml(job.pic_designer_name || "-")}</b> ·
      Judul: <b>${escapeHtml(job.design_title || "-")}</b> ·
      Tipe: <b>${job.job_type === "lembur" ? "Lembur" : "Reguler"}</b> ·
      Status: <b>${STATUS_LABELS[job.status]?.t || job.status}</b>
    </div>
    ${job.description ? `<div class="desc"><b>Keterangan:</b> ${escapeHtml(job.description)}</div>` : ""}
    <div class="grid">${items}</div>
    </body></html>`;
}

function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, c => ({ "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[c]));
}
